import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ShieldCheck,
  Package,
  Truck,
  Globe,
  Plus,
  Edit3,
  Trash2,
  Search,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  LogOut,
  DollarSign,
  ShoppingBag,
  Clock,
  Upload,
  X,
  Star,
  Save,
  RefreshCw,
  Percent,
  MapPin,
  Users,
  UserPlus,
  Key,
  Lock,
  Unlock,
  Mail,
  Trophy,
  Calendar,
  LayoutGrid,
  Tag
} from '../../components/ShopIcons';
import {
  useCart,
  DEFAULT_FALLBACK_PRODUCTS,
  DEFAULT_FALLBACK_SETTINGS,
  DEFAULT_FALLBACK_TAX_ZONES
} from '../../context/CartContext';
import { safeParseJson } from '../../utils/auth';

const EMPTY_PRODUCT_FORM = {
  id: null,
  sku: '',
  name: '',
  slug: '',
  short_description: '',
  description: '',
  price: '24.99',
  compare_at_price: '',
  currency: 'CAD',
  category: 'Books & Journals',
  product_type: 'physical',
  stock_quantity: 50,
  track_inventory: true,
  free_shipping: false,
  primary_image_url: '/Images/cover.jpg',
  gallery_images: ['/Images/cover.jpg'],
  variantsText: '',
  external_buy_url: '',
  external_buy_label: '',
  is_featured: false,
  is_active: true,
  sort_order: 10
};

const EMPTY_DISCOUNT_FORM = {
  id: null,
  title: '',
  discount_type: 'percentage',
  trigger_type: 'automatic',
  code: '',
  discount_value: '15',
  applies_to: 'selected_products',
  product_ids: [],
  min_order_amount: '0',
  no_time_limit: false,
  starts_at: '',
  ends_at: '',
  max_uses: '',
  is_active: true
};

const EMPTY_TAX_ZONE_FORM = {
  id: null,
  name: '',
  country_code: 'CA',
  region_code: '',
  tax_name: 'HST',
  tax_rate: '13.00',
  apply_to_shipping: true,
  is_default: false,
  is_active: true
};

const EMPTY_USER_FORM = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  role: 'user',
  club_name: '',
  utr: '',
  wtn: '',
  is_active: true,
  is_locked: false
};

const DEFAULT_FALLBACK_USERS = [
  {
    id: 1,
    first_name: 'Dima',
    last_name: 'Admin',
    full_name: 'Dima Admin',
    email: 'dimabot@gmail.com',
    club_name: 'Best 3 in Tennis Club',
    utr: '8.5',
    wtn: '22.4',
    role: 'admin',
    is_active: true,
    is_locked: false,
    has_pending_reset: false,
    matches_count: 3,
    completed_matches_count: 2,
    wins_count: 2,
    losses_count: 0,
    opponents_count: 3,
    last_match_date: '2026-10-15 14:30:00',
    created_at: '2026-01-10 09:00:00'
  },
  {
    id: 2,
    first_name: 'Alex',
    last_name: 'Morgan',
    full_name: 'Alex Morgan',
    email: 'alex.player@best3intennis.com',
    club_name: 'Toronto Lawn Tennis Club',
    utr: '7.2',
    wtn: '24.1',
    role: 'user',
    is_active: true,
    is_locked: false,
    has_pending_reset: false,
    matches_count: 2,
    completed_matches_count: 2,
    wins_count: 1,
    losses_count: 1,
    opponents_count: 2,
    last_match_date: '2026-10-16 18:15:00',
    created_at: '2026-03-12 11:20:00'
  }
];

const DEFAULT_FALLBACK_MATCHES = [
  {
    match_id: 101,
    match_name: 'Club Championship Quarterfinal',
    opponent_name: 'Marco Silva',
    opponent_utr: '8.1',
    opponent_wtn: '23.0',
    location: 'Court 1 — Hard Court',
    description: 'Solid first serve percentage and deep crosscourt forehands.',
    status: 'completed',
    winner: 'me',
    score: '6-4, 6-3',
    sets_breakdown: ['6-4', '6-3'],
    my_sets_won: 2,
    opponent_sets_won: 0,
    total_games_logged: 19,
    match_config: { numberOfSets: 3, gamesPerSet: 6, finalSetType: 'super_tiebreak' },
    match_date: '2026-10-15 14:30:00'
  },
  {
    match_id: 102,
    match_name: 'Weekend Ladder Match',
    opponent_name: 'Jordan Lee',
    opponent_utr: '7.8',
    opponent_wtn: '23.5',
    location: 'Indoor Court 3',
    description: 'Recovered from 2-5 down in the second set.',
    status: 'completed',
    winner: 'me',
    score: '6-2, 7-5',
    sets_breakdown: ['6-2', '7-5'],
    my_sets_won: 2,
    opponent_sets_won: 0,
    total_games_logged: 20,
    match_config: { numberOfSets: 3, gamesPerSet: 6, finalSetType: 'full_set' },
    match_date: '2026-10-10 10:00:00'
  }
];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const {
    products: contextProducts,
    setProducts: setContextProducts,
    settings: contextSettings,
    setSettings: setContextSettings,
    taxZones: contextTaxZones,
    setTaxZones: setContextTaxZones,
    discounts: contextDiscounts,
    setDiscounts: setContextDiscounts,
    formatPrice
  } = useCart();

  const [activeTab, setActiveTab] = useState('products');
  const [adminProducts, setAdminProducts] = useState(
    contextProducts && contextProducts.length > 0 ? contextProducts : DEFAULT_FALLBACK_PRODUCTS
  );
  const [orders, setOrders] = useState([]);
  const [storeSettings, setStoreSettings] = useState({
    ...DEFAULT_FALLBACK_SETTINGS,
    ...contextSettings
  });
  const [taxZones, setTaxZones] = useState(
    contextTaxZones && contextTaxZones.length > 0 ? contextTaxZones : DEFAULT_FALLBACK_TAX_ZONES
  );

  // Discounts & Promotions state
  const [discountsList, setDiscountsList] = useState(() => {
    const saved = safeParseJson(localStorage.getItem('best3_local_admin_discounts'));
    if (Array.isArray(saved) && saved.length > 0) return saved;
    if (Array.isArray(contextDiscounts) && contextDiscounts.length > 0) return contextDiscounts;
    return [];
  });
  const [discountTypeFilter, setDiscountTypeFilter] = useState('all');
  const [discountSearch, setDiscountSearch] = useState('');
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState(EMPTY_DISCOUNT_FORM);

  // User Management state
  const [usersList, setUsersList] = useState(() => {
    const saved = safeParseJson(localStorage.getItem('best3_local_admin_users'));
    return Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_FALLBACK_USERS;
  });
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [isUserFormModalOpen, setIsUserFormModalOpen] = useState(false);
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);

  // Password Reset modal state
  const [passwordResetModalUser, setPasswordResetModalUser] = useState(null);
  const [directNewPassword, setDirectNewPassword] = useState('');
  const [sendingResetEmailId, setSendingResetEmailId] = useState(null);

  // Player Matches Grid Overlay state
  const [selectedPlayerOverlay, setSelectedPlayerOverlay] = useState(null);
  const [matchesOverlaySearch, setMatchesOverlaySearch] = useState('');
  const [matchesOverlayStatusFilter, setMatchesOverlayStatusFilter] = useState('all');

  const [loading, setLoading] = useState(false);
  const [bannerMessage, setBannerMessage] = useState(null);

  // Product filtering & modal state
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('All');
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT_FORM);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Order filtering & modal state
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderUpdateForm, setOrderUpdateForm] = useState({
    payment_status: 'paid',
    fulfillment_status: 'processing',
    tracking_carrier: 'Canada Post',
    tracking_number: '',
    tracking_url: '',
    admin_notes: '',
    send_shipping_email: true
  });

  // Tax Zone modal/form state
  const [isTaxZoneModalOpen, setIsTaxZoneModalOpen] = useState(false);
  const [taxZoneForm, setTaxZoneForm] = useState(EMPTY_TAX_ZONE_FORM);

  // PayPal Configuration & Refresh state
  const [paypalStatus, setPaypalStatus] = useState({
    enabled: storeSettings.paypal_enabled !== false,
    mode: storeSettings.paypal_mode || 'sandbox',
    merchant_name: storeSettings.paypal_merchant_name || 'Best 3 in Tennis Pro Shop',
    merchant_email: storeSettings.paypal_merchant_email || 'dimabot@gmail.com',
    support_email: storeSettings.paypal_support_email || 'support@best3intennis.com',
    order_notification_email: storeSettings.paypal_order_notification_email || storeSettings.paypal_merchant_email || 'dimabot@gmail.com',
    allow_card_funding: storeSettings.paypal_allow_card_funding !== false,
    currency: storeSettings.default_currency || 'CAD',
    refreshed_at: new Date().toISOString()
  });
  const [refreshingPaypal, setRefreshingPaypal] = useState(false);

  const token = localStorage.getItem('accessToken');
  const userRole = localStorage.getItem('userRole');

  useEffect(() => {
    if (!token || userRole !== 'admin') {
      navigate('/admin/login', { replace: true });
    }
  }, [token, userRole, navigate]);

  const showNotice = useCallback((text, type = 'success') => {
    setBannerMessage({ text, type });
    setTimeout(() => {
      setBannerMessage((prev) => (prev && prev.text === text ? null : prev));
    }, 5000);
  }, []);

  const loadAdminData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // 1. Load products
      try {
        const prodRes = await fetch('/best3intennis-api/api/admin_products.php', { headers });
        if (prodRes.ok) {
          const prodData = safeParseJson(await prodRes.text());
          if (prodData && prodData.success && Array.isArray(prodData.products)) {
            setAdminProducts(prodData.products);
            setContextProducts(prodData.products);
            localStorage.setItem('best3_local_admin_products', JSON.stringify(prodData.products));
          }
        }
      } catch (_e) {
        const localProds = safeParseJson(localStorage.getItem('best3_local_admin_products'));
        if (Array.isArray(localProds) && localProds.length > 0) {
          setAdminProducts(localProds);
        }
      }

      // 2. Load orders, settings, tax zones, paypal_status
      let apiOrders = [];
      try {
        const ordRes = await fetch('/best3intennis-api/api/admin_orders.php', { headers });
        if (ordRes.ok) {
          const ordData = safeParseJson(await ordRes.text());
          if (ordData && ordData.success) {
            if (Array.isArray(ordData.orders)) {
              apiOrders = ordData.orders;
            }
            if (ordData.settings) {
              setStoreSettings((prev) => ({ ...prev, ...ordData.settings }));
              setContextSettings((prev) => ({ ...prev, ...ordData.settings }));
              localStorage.setItem('best3_local_admin_settings', JSON.stringify(ordData.settings));
            }
            if (ordData.paypal_status) {
              setPaypalStatus((prev) => ({ ...prev, ...ordData.paypal_status }));
            } else if (ordData.settings) {
              setPaypalStatus((prev) => ({
                ...prev,
                enabled: ordData.settings.paypal_enabled !== false,
                mode: ordData.settings.paypal_mode || 'sandbox',
                merchant_name: ordData.settings.paypal_merchant_name || 'Best 3 in Tennis Pro Shop',
                merchant_email: ordData.settings.paypal_merchant_email || 'dimabot@gmail.com',
                support_email: ordData.settings.paypal_support_email || 'support@best3intennis.com',
                order_notification_email: ordData.settings.paypal_order_notification_email || ordData.settings.paypal_merchant_email || 'dimabot@gmail.com',
                allow_card_funding: ordData.settings.paypal_allow_card_funding !== false,
                currency: ordData.settings.default_currency || 'CAD',
                refreshed_at: new Date().toISOString()
              }));
            }
            if (Array.isArray(ordData.tax_zones) && ordData.tax_zones.length > 0) {
              setTaxZones(ordData.tax_zones);
              setContextTaxZones(ordData.tax_zones);
              localStorage.setItem('best3_local_admin_tax_zones', JSON.stringify(ordData.tax_zones));
            }
          }
        }
      } catch (_e) {
        // Fallback to local storage orders
      }

      const localOrders = safeParseJson(localStorage.getItem('best3_local_orders_v1')) || [];
      const mergedMap = new Map();
      apiOrders.forEach((o) => mergedMap.set(o.order_number, o));
      if (Array.isArray(localOrders)) {
        localOrders.forEach((o) => {
          if (o && o.order_number && !mergedMap.has(o.order_number)) {
            mergedMap.set(o.order_number, o);
          }
        });
      }
      setOrders(Array.from(mergedMap.values()));

      // 3. Load users (TennisLog Players & Admin Users)
      try {
        const usrRes = await fetch('/best3intennis-api/api/admin_users.php', { headers });
        if (usrRes.ok) {
          const usrData = safeParseJson(await usrRes.text());
          if (usrData && usrData.success && Array.isArray(usrData.users)) {
            setUsersList(usrData.users);
            localStorage.setItem('best3_local_admin_users', JSON.stringify(usrData.users));
          }
        }
      } catch (_e) {
        // Keep fallback or cached users
      }

      // 4. Load discounts & promotions
      try {
        const discRes = await fetch('/best3intennis-api/api/admin_discounts.php', { headers });
        if (discRes.ok) {
          const discData = safeParseJson(await discRes.text());
          if (discData && discData.success && Array.isArray(discData.discounts)) {
            setDiscountsList(discData.discounts);
            if (setContextDiscounts) setContextDiscounts(discData.discounts);
            localStorage.setItem('best3_local_admin_discounts', JSON.stringify(discData.discounts));
          }
        }
      } catch (_e) {
        // Keep fallback or cached discounts
      }
    } finally {
      setLoading(false);
    }
  }, [token, setContextProducts, setContextSettings, setContextTaxZones, setContextDiscounts]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('adminUser');
    navigate('/admin/login', { replace: true });
  };

  // KPI Metrics
  const kpiStats = useMemo(() => {
    const paidOrders = orders.filter(
      (o) => o.payment_status === 'paid' || o.payment_status === 'COMPLETED'
    );
    const totalRevenue = paidOrders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
    const pendingFulfillment = orders.filter(
      (o) =>
        o.fulfillment_status === 'unfulfilled' ||
        o.fulfillment_status === 'processing' ||
        !o.fulfillment_status
    ).length;
    const lowStockCount = adminProducts.filter(
      (p) => p.track_inventory && Number(p.stock_quantity) <= 5
    ).length;

    return {
      totalRevenue,
      paidCount: paidOrders.length,
      totalOrders: orders.length,
      pendingFulfillment,
      activeProducts: adminProducts.filter((p) => p.is_active).length,
      lowStockCount
    };
  }, [orders, adminProducts]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    return adminProducts.filter((p) => {
      const matchesCategory =
        productCategoryFilter === 'All' || p.category === productCategoryFilter;
      const q = productSearch.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [adminProducts, productCategoryFilter, productSearch]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchesStatus =
        orderStatusFilter === 'all' || o.fulfillment_status === orderStatusFilter;
      const q = orderSearch.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (o.order_number && o.order_number.toLowerCase().includes(q)) ||
        (o.customer_name && o.customer_name.toLowerCase().includes(q)) ||
        (o.customer_email && o.customer_email.toLowerCase().includes(q));
      return matchesStatus && matchesSearch;
    });
  }, [orders, orderStatusFilter, orderSearch]);

  // Open Product Modal for Create or Edit
  const openCreateProductModal = () => {
    setProductForm({
      ...EMPTY_PRODUCT_FORM,
      currency: storeSettings.default_currency || 'CAD',
      sku: `B3T-NEW-${Math.floor(100 + Math.random() * 900)}`
    });
    setNewGalleryUrl('');
    setIsProductModalOpen(true);
  };

  const openEditProductModal = (prod) => {
    let variantsStr = '';
    if (Array.isArray(prod.variants) && prod.variants.length > 0) {
      const firstGroup = prod.variants[0];
      if (firstGroup && Array.isArray(firstGroup.options)) {
        variantsStr = firstGroup.options.join(', ');
      }
    }
    setProductForm({
      id: prod.id,
      sku: prod.sku || '',
      name: prod.name || '',
      slug: prod.slug || '',
      short_description: prod.short_description || '',
      description: prod.description || '',
      price: String(prod.price ?? '24.99'),
      compare_at_price: prod.compare_at_price ? String(prod.compare_at_price) : '',
      currency: prod.currency || storeSettings.default_currency || 'CAD',
      category: prod.category || 'Books & Journals',
      product_type: prod.product_type || 'physical',
      stock_quantity: prod.stock_quantity ?? 50,
      track_inventory: Boolean(prod.track_inventory),
      free_shipping: Boolean(prod.free_shipping),
      primary_image_url: prod.primary_image_url || '/Images/cover.jpg',
      gallery_images:
        Array.isArray(prod.gallery_images) && prod.gallery_images.length > 0
          ? prod.gallery_images
          : [prod.primary_image_url || '/Images/cover.jpg'],
      variantsText: variantsStr,
      external_buy_url: prod.external_buy_url || '',
      external_buy_label: prod.external_buy_label || '',
      is_featured: Boolean(prod.is_featured),
      is_active: prod.is_active !== false,
      sort_order: prod.sort_order ?? 10
    });
    setNewGalleryUrl('');
    setIsProductModalOpen(true);
  };

  const handleImageFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      let uploadedUrl = '';
      try {
        const res = await fetch('/best3intennis-api/api/admin_upload_image.php', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        const data = safeParseJson(await res.text());
        if (res.ok && data && data.success && data.image_url) {
          uploadedUrl = data.image_url;
        }
      } catch (_err) {
        // Fallback to DataURL in local pre-FTP testing
      }

      if (!uploadedUrl) {
        uploadedUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      setProductForm((prev) => {
        const gallery = Array.isArray(prev.gallery_images) ? [...prev.gallery_images] : [];
        if (!gallery.includes(uploadedUrl)) {
          gallery.unshift(uploadedUrl);
        }
        return {
          ...prev,
          primary_image_url: uploadedUrl,
          gallery_images: gallery
        };
      });
      showNotice('Product image uploaded and added to gallery.');
    } catch (_err) {
      showNotice('Could not upload image file.', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddGalleryUrl = () => {
    const trimmed = newGalleryUrl.trim();
    if (!trimmed) return;
    setProductForm((prev) => {
      const gallery = Array.isArray(prev.gallery_images) ? [...prev.gallery_images] : [];
      if (!gallery.includes(trimmed)) {
        gallery.push(trimmed);
      }
      return {
        ...prev,
        primary_image_url: prev.primary_image_url || trimmed,
        gallery_images: gallery
      };
    });
    setNewGalleryUrl('');
  };

  const handleRemoveGalleryImage = (urlToRemove) => {
    setProductForm((prev) => {
      const filtered = (prev.gallery_images || []).filter((u) => u !== urlToRemove);
      const nextPrimary =
        prev.primary_image_url === urlToRemove
          ? filtered[0] || '/Images/cover.jpg'
          : prev.primary_image_url;
      return {
        ...prev,
        primary_image_url: nextPrimary,
        gallery_images: filtered.length > 0 ? filtered : [nextPrimary]
      };
    });
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name.trim()) {
      showNotice('Product name is required.', 'error');
      return;
    }

    const parsedVariants = productForm.variantsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      id: productForm.id,
      sku: productForm.sku.trim() || `B3T-${Date.now()}`,
      name: productForm.name.trim(),
      slug:
        productForm.slug.trim() ||
        productForm.name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-'),
      short_description: productForm.short_description.trim(),
      description: productForm.description.trim(),
      price: Number(productForm.price || 0),
      compare_at_price:
        productForm.compare_at_price !== '' ? Number(productForm.compare_at_price) : null,
      currency: productForm.currency || storeSettings.default_currency || 'CAD',
      category: productForm.category,
      product_type: productForm.product_type,
      stock_quantity: Number(productForm.stock_quantity || 0),
      track_inventory: Boolean(productForm.track_inventory),
      free_shipping: Boolean(productForm.free_shipping),
      primary_image_url: productForm.primary_image_url || '/Images/cover.jpg',
      gallery_images:
        productForm.gallery_images && productForm.gallery_images.length > 0
          ? productForm.gallery_images
          : [productForm.primary_image_url || '/Images/cover.jpg'],
      variants: parsedVariants.length > 0 ? [{ name: 'Option', options: parsedVariants }] : [],
      external_buy_url: productForm.external_buy_url.trim() || null,
      external_buy_label: productForm.external_buy_label.trim() || null,
      is_featured: Boolean(productForm.is_featured),
      is_active: Boolean(productForm.is_active),
      sort_order: Number(productForm.sort_order || 10)
    };

    let savedViaApi = false;
    try {
      const res = await fetch('/best3intennis-api/api/admin_products.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: payload.id ? 'update' : 'create',
          ...payload
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && data.product) {
        setAdminProducts((prev) => {
          const exists = prev.some((p) => Number(p.id) === Number(data.product.id));
          const updated = exists
            ? prev.map((p) => (Number(p.id) === Number(data.product.id) ? data.product : p))
            : [...prev, data.product];
          setContextProducts(updated);
          localStorage.setItem('best3_local_admin_products', JSON.stringify(updated));
          return updated;
        });
        savedViaApi = true;
      }
    } catch (_err) {
      // Local state update fallback below
    }

    if (!savedViaApi) {
      const assignedId = payload.id || Date.now();
      const newProductObj = { ...payload, id: assignedId };
      setAdminProducts((prev) => {
        const exists = prev.some((p) => Number(p.id) === Number(assignedId));
        const updated = exists
          ? prev.map((p) => (Number(p.id) === Number(assignedId) ? newProductObj : p))
          : [...prev, newProductObj];
        setContextProducts(updated);
        localStorage.setItem('best3_local_admin_products', JSON.stringify(updated));
        return updated;
      });
    }

    setIsProductModalOpen(false);
    showNotice(
      payload.id
        ? `Updated product "${payload.name}".`
        : `Created new product "${payload.name}".`
    );
  };

  const handleToggleProductField = async (prod, field) => {
    const updatedProduct = { ...prod, [field]: !prod[field] };
    try {
      await fetch('/best3intennis-api/api/admin_products.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'toggle_status',
          id: prod.id,
          field,
          value: !prod[field]
        })
      });
    } catch (_e) {}

    setAdminProducts((prev) => {
      const next = prev.map((p) => (p.id === prod.id ? updatedProduct : p));
      setContextProducts(next);
      localStorage.setItem('best3_local_admin_products', JSON.stringify(next));
      return next;
    });
    showNotice(`Updated "${prod.name}".`);
  };

  const handleDeleteProduct = async (prod) => {
    try {
      await fetch('/best3intennis-api/api/admin_products.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete',
          id: prod.id
        })
      });
    } catch (_e) {}

    setAdminProducts((prev) => {
      const next = prev.filter((p) => p.id !== prod.id);
      setContextProducts(next);
      localStorage.setItem('best3_local_admin_products', JSON.stringify(next));
      return next;
    });
    showNotice(`Removed "${prod.name}" from catalog.`);
  };

  // Order detail & fulfillment update
  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setOrderUpdateForm({
      payment_status: order.payment_status || 'paid',
      fulfillment_status: order.fulfillment_status || 'processing',
      tracking_carrier: order.tracking_carrier || 'Canada Post',
      tracking_number: order.tracking_number || '',
      tracking_url: order.tracking_url || '',
      admin_notes: order.admin_notes || '',
      send_shipping_email: true
    });
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;

    const payload = {
      action: 'update_order',
      order_id: selectedOrder.id,
      order_number: selectedOrder.order_number,
      ...orderUpdateForm
    };

    try {
      await fetch('/best3intennis-api/api/admin_orders.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (_e) {}

    const updatedOrder = {
      ...selectedOrder,
      payment_status: orderUpdateForm.payment_status,
      fulfillment_status: orderUpdateForm.fulfillment_status,
      tracking_carrier: orderUpdateForm.tracking_carrier,
      tracking_number: orderUpdateForm.tracking_number,
      tracking_url: orderUpdateForm.tracking_url,
      admin_notes: orderUpdateForm.admin_notes,
      shipped_at:
        orderUpdateForm.fulfillment_status === 'shipped'
          ? selectedOrder.shipped_at || new Date().toISOString()
          : selectedOrder.shipped_at,
      delivered_at:
        orderUpdateForm.fulfillment_status === 'delivered'
          ? selectedOrder.delivered_at || new Date().toISOString()
          : selectedOrder.delivered_at
    };

    setOrders((prev) => {
      const next = prev.map((o) =>
        o.order_number === selectedOrder.order_number ? updatedOrder : o
      );
      localStorage.setItem('best3_local_orders_v1', JSON.stringify(next));
      return next;
    });

    setSelectedOrder(updatedOrder);
    showNotice(`Order ${selectedOrder.order_number} fulfillment updated to "${orderUpdateForm.fulfillment_status}".`);
  };

  // Store Currency & Shipping Settings Save
  const handleSaveStoreSettings = async (e) => {
    e.preventDefault();
    const updatedSettings = {
      ...storeSettings,
      default_tax_rate: Number(storeSettings.default_tax_rate || 13),
      flat_shipping_rate: Number(storeSettings.flat_shipping_rate || 0),
      free_shipping_threshold: Number(storeSettings.free_shipping_threshold || 0)
    };

    try {
      await fetch('/best3intennis-api/api/admin_orders.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_settings',
          ...updatedSettings
        })
      });
    } catch (_e) {}

    setStoreSettings(updatedSettings);
    setContextSettings(updatedSettings);
    localStorage.setItem('best3_local_admin_settings', JSON.stringify(updatedSettings));
    showNotice(
      `Store settings saved (Default Currency: ${updatedSettings.default_currency}, Default Tax: ${updatedSettings.default_tax_name} ${updatedSettings.default_tax_rate}%).`
    );
  };

  // PayPal Configuration Save & Refresh
  const handleSavePaypalConfig = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const supportEmail = (storeSettings.paypal_support_email || 'support@best3intennis.com').trim();
    const orderNotificationEmail = (storeSettings.paypal_order_notification_email || storeSettings.paypal_merchant_email || 'dimabot@gmail.com').trim();
    const updatedSettings = {
      ...storeSettings,
      paypal_enabled: storeSettings.paypal_enabled !== false,
      paypal_mode: storeSettings.paypal_mode === 'live' ? 'live' : 'sandbox',
      paypal_merchant_name: (storeSettings.paypal_merchant_name || 'Best 3 in Tennis Pro Shop').trim(),
      paypal_merchant_email: orderNotificationEmail,
      paypal_support_email: supportEmail,
      paypal_order_notification_email: orderNotificationEmail,
      paypal_allow_card_funding: storeSettings.paypal_allow_card_funding !== false
    };

    try {
      const res = await fetch('/best3intennis-api/api/admin_orders.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_paypal_settings',
          paypal_enabled: updatedSettings.paypal_enabled,
          paypal_mode: updatedSettings.paypal_mode,
          paypal_merchant_name: updatedSettings.paypal_merchant_name,
          paypal_merchant_email: updatedSettings.paypal_merchant_email,
          paypal_support_email: updatedSettings.paypal_support_email,
          paypal_order_notification_email: updatedSettings.paypal_order_notification_email,
          paypal_allow_card_funding: updatedSettings.paypal_allow_card_funding
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success) {
        if (data.settings) {
          Object.assign(updatedSettings, data.settings);
        }
        if (data.paypal_status) {
          setPaypalStatus((prev) => ({ ...prev, ...data.paypal_status }));
        }
      }
    } catch (_e) {}

    setStoreSettings(updatedSettings);
    setContextSettings(updatedSettings);
    setPaypalStatus((prev) => ({
      ...prev,
      enabled: updatedSettings.paypal_enabled,
      mode: updatedSettings.paypal_mode,
      merchant_name: updatedSettings.paypal_merchant_name,
      merchant_email: updatedSettings.paypal_merchant_email,
      support_email: updatedSettings.paypal_support_email,
      order_notification_email: updatedSettings.paypal_order_notification_email,
      allow_card_funding: updatedSettings.paypal_allow_card_funding,
      currency: updatedSettings.default_currency || 'CAD',
      refreshed_at: new Date().toISOString()
    }));
    localStorage.setItem('best3_local_admin_settings', JSON.stringify(updatedSettings));
    showNotice(
      `PayPal configuration saved (Mode: ${updatedSettings.paypal_mode.toUpperCase()}, Status: ${
        updatedSettings.paypal_enabled ? 'Enabled' : 'Disabled'
      }).`
    );
  };

  const handleRefreshPaypalConfig = async () => {
    setRefreshingPaypal(true);
    try {
      const res = await fetch('/best3intennis-api/api/admin_orders.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'refresh_paypal_status' })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success) {
        if (data.settings) {
          setStoreSettings((prev) => {
            const merged = { ...prev, ...data.settings };
            setContextSettings(merged);
            localStorage.setItem('best3_local_admin_settings', JSON.stringify(merged));
            return merged;
          });
        }
        if (data.paypal_status) {
          setPaypalStatus((prev) => ({
            ...prev,
            ...data.paypal_status,
            refreshed_at: data.paypal_status.refreshed_at || new Date().toISOString()
          }));
        }
        showNotice('PayPal configuration and server environment refreshed.');
        setRefreshingPaypal(false);
        return;
      }
    } catch (_e) {}

    setPaypalStatus((prev) => ({
      ...prev,
      enabled: storeSettings.paypal_enabled !== false,
      mode: storeSettings.paypal_mode || 'sandbox',
      merchant_name: storeSettings.paypal_merchant_name || 'Best 3 in Tennis Pro Shop',
      merchant_email: storeSettings.paypal_merchant_email || 'dimabot@gmail.com',
      support_email: storeSettings.paypal_support_email || 'support@best3intennis.com',
      order_notification_email: storeSettings.paypal_order_notification_email || storeSettings.paypal_merchant_email || 'dimabot@gmail.com',
      allow_card_funding: storeSettings.paypal_allow_card_funding !== false,
      currency: storeSettings.default_currency || 'CAD',
      refreshed_at: new Date().toISOString()
    }));
    showNotice('PayPal configuration refreshed.');
    setRefreshingPaypal(false);
  };

  // Tax Zone CRUD
  const openCreateTaxZoneModal = () => {
    setTaxZoneForm(EMPTY_TAX_ZONE_FORM);
    setIsTaxZoneModalOpen(true);
  };

  const openEditTaxZoneModal = (zone) => {
    setTaxZoneForm({
      id: zone.id,
      name: zone.name || '',
      country_code: zone.country_code || 'CA',
      region_code: zone.region_code || '*',
      tax_name: zone.tax_name || 'HST',
      tax_rate: String(zone.tax_rate ?? '13.00'),
      apply_to_shipping: zone.apply_to_shipping !== false,
      is_default: Boolean(zone.is_default),
      is_active: zone.is_active !== false
    });
    setIsTaxZoneModalOpen(true);
  };

  const handleSaveTaxZone = async (e) => {
    e.preventDefault();
    if (!taxZoneForm.name.trim()) {
      showNotice('Tax zone name is required.', 'error');
      return;
    }

    const action = taxZoneForm.id ? 'update_tax_zone' : 'create_tax_zone';
    const zonePayload = {
      action,
      id: taxZoneForm.id,
      name: taxZoneForm.name.trim(),
      country_code: (taxZoneForm.country_code || 'CA').toUpperCase().trim(),
      region_code: (taxZoneForm.region_code || '*').toUpperCase().trim(),
      tax_name: (taxZoneForm.tax_name || 'HST').trim(),
      tax_rate: Number(taxZoneForm.tax_rate || 0),
      apply_to_shipping: Boolean(taxZoneForm.apply_to_shipping),
      is_default: Boolean(taxZoneForm.is_default),
      is_active: Boolean(taxZoneForm.is_active)
    };

    let updatedZonesFromApi = null;
    try {
      const res = await fetch('/best3intennis-api/api/admin_orders.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(zonePayload)
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.tax_zones)) {
        updatedZonesFromApi = data.tax_zones;
      }
    } catch (_e) {}

    if (updatedZonesFromApi) {
      setTaxZones(updatedZonesFromApi);
      setContextTaxZones(updatedZonesFromApi);
      localStorage.setItem('best3_local_admin_tax_zones', JSON.stringify(updatedZonesFromApi));
    } else {
      const assignedId = zonePayload.id || Date.now();
      const newZone = { ...zonePayload, id: assignedId };
      setTaxZones((prev) => {
        let list = prev.map((z) =>
          zonePayload.is_default ? { ...z, is_default: false } : z
        );
        const exists = list.some((z) => Number(z.id) === Number(assignedId));
        list = exists
          ? list.map((z) => (Number(z.id) === Number(assignedId) ? newZone : z))
          : [...list, newZone];
        setContextTaxZones(list);
        localStorage.setItem('best3_local_admin_tax_zones', JSON.stringify(list));
        return list;
      });
    }

    setIsTaxZoneModalOpen(false);
    showNotice(`Saved tax zone "${zonePayload.name}" (${zonePayload.tax_name} ${zonePayload.tax_rate}%).`);
  };

  const handleSetDefaultTaxZone = async (zone) => {
    try {
      await fetch('/best3intennis-api/api/admin_orders.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'set_default_tax_zone', id: zone.id })
      });
    } catch (_e) {}

    setTaxZones((prev) => {
      const next = prev.map((z) => ({
        ...z,
        is_default: Number(z.id) === Number(zone.id)
      }));
      setContextTaxZones(next);
      localStorage.setItem('best3_local_admin_tax_zones', JSON.stringify(next));
      return next;
    });

    const nextSettings = {
      ...storeSettings,
      default_tax_name: zone.tax_name,
      default_tax_rate: Number(zone.tax_rate)
    };
    setStoreSettings(nextSettings);
    setContextSettings(nextSettings);
    localStorage.setItem('best3_local_admin_settings', JSON.stringify(nextSettings));
    showNotice(`Set "${zone.name}" (${zone.tax_name} ${zone.tax_rate}%) as the default store tax zone.`);
  };

  const handleDeleteTaxZone = async (zone) => {
    if (zone.is_default) {
      showNotice('Cannot delete the default tax zone. Assign another default zone first.', 'error');
      return;
    }
    try {
      await fetch('/best3intennis-api/api/admin_orders.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'delete_tax_zone', id: zone.id })
      });
    } catch (_e) {}

    setTaxZones((prev) => {
      const next = prev.filter((z) => Number(z.id) !== Number(zone.id));
      setContextTaxZones(next);
      localStorage.setItem('best3_local_admin_tax_zones', JSON.stringify(next));
      return next;
    });
    showNotice(`Deleted tax zone "${zone.name}".`);
  };

  // User Management Filtering & Actions
  const filteredUsers = useMemo(() => {
    return usersList.filter((u) => {
      if (userRoleFilter === 'user' && u.role === 'admin') return false;
      if (userRoleFilter === 'admin' && u.role !== 'admin') return false;
      if (userRoleFilter === 'locked' && !u.is_locked) return false;

      const q = userSearch.trim().toLowerCase();
      if (!q) return true;
      const fullName = `${u.first_name || ''} ${u.last_name || ''} ${u.full_name || ''}`.toLowerCase();
      return (
        fullName.includes(q) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.club_name && u.club_name.toLowerCase().includes(q))
      );
    });
  }, [usersList, userRoleFilter, userSearch]);

  const filteredOverlayMatches = useMemo(() => {
    if (!selectedPlayerOverlay || !Array.isArray(selectedPlayerOverlay.matches)) return [];
    return selectedPlayerOverlay.matches.filter((m) => {
      if (matchesOverlayStatusFilter !== 'all' && m.status !== matchesOverlayStatusFilter) {
        return false;
      }
      const q = matchesOverlaySearch.trim().toLowerCase();
      if (!q) return true;
      return (
        (m.match_name && m.match_name.toLowerCase().includes(q)) ||
        (m.opponent_name && m.opponent_name.toLowerCase().includes(q)) ||
        (m.location && m.location.toLowerCase().includes(q)) ||
        (m.score && m.score.toLowerCase().includes(q))
      );
    });
  }, [selectedPlayerOverlay, matchesOverlayStatusFilter, matchesOverlaySearch]);

  const handleOpenUserMatches = async (userObj) => {
    if (!userObj) return;
    setMatchesOverlaySearch('');
    setMatchesOverlayStatusFilter('all');
    setSelectedPlayerOverlay({
      user: userObj,
      matches: [],
      summary: {
        total_matches: userObj.matches_count || 0,
        completed_matches: userObj.completed_matches_count || 0,
        in_progress_matches: Math.max(0, (userObj.matches_count || 0) - (userObj.completed_matches_count || 0)),
        wins: userObj.wins_count || 0,
        losses: userObj.losses_count || 0,
        win_rate:
          (userObj.wins_count || 0) + (userObj.losses_count || 0) > 0
            ? Math.round(((userObj.wins_count || 0) / ((userObj.wins_count || 0) + (userObj.losses_count || 0))) * 100)
            : 0
      },
      loading: true
    });

    try {
      const res = await fetch(
        `/best3intennis-api/api/admin_users.php?action=user_matches&user_id=${encodeURIComponent(userObj.id)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (res.ok) {
        const data = safeParseJson(await res.text());
        if (data && data.success && Array.isArray(data.matches)) {
          setSelectedPlayerOverlay({
            user: data.user || userObj,
            matches: data.matches,
            summary: data.summary || {
              total_matches: data.matches.length,
              completed_matches: data.matches.filter((m) => m.status === 'completed').length,
              in_progress_matches: data.matches.filter((m) => m.status !== 'completed').length,
              wins: data.matches.filter((m) => m.winner === 'me').length,
              losses: data.matches.filter((m) => m.winner === 'opponent').length,
              win_rate: 0
            },
            loading: false
          });
          return;
        }
      }
    } catch (_e) {
      // Fallback to local sample matches if offline
    }

    setSelectedPlayerOverlay({
      user: userObj,
      matches: DEFAULT_FALLBACK_MATCHES,
      summary: {
        total_matches: DEFAULT_FALLBACK_MATCHES.length,
        completed_matches: DEFAULT_FALLBACK_MATCHES.length,
        in_progress_matches: 0,
        wins: DEFAULT_FALLBACK_MATCHES.length,
        losses: 0,
        win_rate: 100
      },
      loading: false
    });
  };

  const openCreateUserModal = (defaultRole = 'user') => {
    setUserForm({
      ...EMPTY_USER_FORM,
      role: defaultRole
    });
    setIsUserFormModalOpen(true);
  };

  const openEditUserModal = (u) => {
    setUserForm({
      id: u.id,
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      email: u.email || '',
      password: '',
      role: u.role === 'admin' ? 'admin' : 'user',
      club_name: u.club_name || '',
      utr: u.utr || '',
      wtn: u.wtn || '',
      is_active: u.is_active !== false,
      is_locked: Boolean(u.is_locked)
    });
    setIsUserFormModalOpen(true);
  };

  const handleSaveUserForm = async (e) => {
    e.preventDefault();
    if (!userForm.first_name.trim() || !userForm.email.trim()) {
      showNotice('First name and email address are required.', 'error');
      return;
    }
    if (!userForm.id && userForm.password.trim().length < 6) {
      showNotice('Password must be at least 6 characters for a new user.', 'error');
      return;
    }

    const isCreating = !userForm.id;
    const payload = {
      action: isCreating ? 'create_user' : 'update_user',
      user_id: userForm.id,
      first_name: userForm.first_name.trim(),
      last_name: userForm.last_name.trim(),
      email: userForm.email.trim(),
      password: userForm.password.trim(),
      role: userForm.role === 'admin' ? 'admin' : 'user',
      club_name: userForm.club_name.trim(),
      utr: userForm.utr.trim(),
      wtn: userForm.wtn.trim(),
      is_active: Boolean(userForm.is_active),
      is_locked: Boolean(userForm.is_locked)
    };

    let updatedFromApi = null;
    try {
      const res = await fetch('/best3intennis-api/api/admin_users.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.users)) {
        updatedFromApi = data.users;
      } else if (data && data.message && !res.ok) {
        showNotice(data.message, 'error');
        return;
      }
    } catch (_e) {}

    if (updatedFromApi) {
      setUsersList(updatedFromApi);
      localStorage.setItem('best3_local_admin_users', JSON.stringify(updatedFromApi));
    } else {
      const assignedId = userForm.id || Date.now();
      const fullName = `${payload.first_name} ${payload.last_name}`.trim();
      const localUser = {
        id: assignedId,
        first_name: payload.first_name,
        last_name: payload.last_name,
        full_name: fullName,
        email: payload.email,
        role: payload.role,
        club_name: payload.club_name,
        utr: payload.utr,
        wtn: payload.wtn,
        is_active: payload.is_active,
        is_locked: payload.is_locked,
        matches_count: 0,
        completed_matches_count: 0,
        wins_count: 0,
        losses_count: 0,
        opponents_count: 0,
        created_at: new Date().toISOString()
      };
      setUsersList((prev) => {
        const exists = prev.some((u) => Number(u.id) === Number(assignedId));
        const next = exists
          ? prev.map((u) => (Number(u.id) === Number(assignedId) ? { ...u, ...localUser } : u))
          : [localUser, ...prev];
        localStorage.setItem('best3_local_admin_users', JSON.stringify(next));
        return next;
      });
    }

    setIsUserFormModalOpen(false);
    showNotice(
      isCreating
        ? `Created ${payload.role === 'admin' ? 'Admin' : 'TennisLog'} account for ${payload.email}.`
        : `Updated user account ${payload.email}.`
    );
  };

  const handleToggleUserLock = async (userObj) => {
    if (!userObj) return;
    const nextLocked = !userObj.is_locked;

    let updatedFromApi = null;
    try {
      const res = await fetch('/best3intennis-api/api/admin_users.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'toggle_lock',
          user_id: userObj.id,
          is_locked: nextLocked
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.users)) {
        updatedFromApi = data.users;
      } else if (data && data.message && !res.ok) {
        showNotice(data.message, 'error');
        return;
      }
    } catch (_e) {}

    if (updatedFromApi) {
      setUsersList(updatedFromApi);
      localStorage.setItem('best3_local_admin_users', JSON.stringify(updatedFromApi));
    } else {
      setUsersList((prev) => {
        const next = prev.map((u) =>
          Number(u.id) === Number(userObj.id)
            ? { ...u, is_locked: nextLocked, is_active: nextLocked ? u.is_active : true }
            : u
        );
        localStorage.setItem('best3_local_admin_users', JSON.stringify(next));
        return next;
      });
    }

    showNotice(
      `Account ${userObj.email} has been ${nextLocked ? 'locked' : 'unlocked'}.`
    );
  };

  const openPasswordResetModal = (userObj) => {
    setPasswordResetModalUser(userObj);
    setDirectNewPassword('');
  };

  const handleDirectPasswordReset = async (e) => {
    e.preventDefault();
    if (!passwordResetModalUser) return;
    if (directNewPassword.trim().length < 6) {
      showNotice('New password must be at least 6 characters.', 'error');
      return;
    }

    try {
      const res = await fetch('/best3intennis-api/api/admin_users.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'reset_password_direct',
          user_id: passwordResetModalUser.id,
          new_password: directNewPassword.trim()
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.users)) {
        setUsersList(data.users);
        localStorage.setItem('best3_local_admin_users', JSON.stringify(data.users));
      }
    } catch (_e) {}

    const targetEmail = passwordResetModalUser.email;
    setPasswordResetModalUser(null);
    setDirectNewPassword('');
    showNotice(`Password has been directly reset for ${targetEmail}.`);
  };

  const handleSendPasswordResetEmail = async (userObj) => {
    if (!userObj) return;
    setSendingResetEmailId(userObj.id);
    try {
      const res = await fetch('/best3intennis-api/api/admin_users.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'send_password_reset_email',
          user_id: userObj.id
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.users)) {
        setUsersList(data.users);
        localStorage.setItem('best3_local_admin_users', JSON.stringify(data.users));
      } else {
        setUsersList((prev) =>
          prev.map((u) =>
            Number(u.id) === Number(userObj.id) ? { ...u, has_pending_reset: true } : u
          )
        );
      }
    } catch (_e) {
      setUsersList((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(userObj.id) ? { ...u, has_pending_reset: true } : u
        )
      );
    } finally {
      setSendingResetEmailId(null);
    }

    if (passwordResetModalUser && Number(passwordResetModalUser.id) === Number(userObj.id)) {
      setPasswordResetModalUser(null);
    }
    showNotice(`Password reset email initiated and sent to ${userObj.email}.`);
  };

  const handleDeleteUser = async (userObj) => {
    if (!userObj) return;
    if (userObj.email && userObj.email.toLowerCase() === 'dimabot@gmail.com') {
      showNotice('Primary administrator account (dimabot@gmail.com) cannot be deleted.', 'error');
      return;
    }

    try {
      const res = await fetch('/best3intennis-api/api/admin_users.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete_user',
          user_id: userObj.id
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.users)) {
        setUsersList(data.users);
        localStorage.setItem('best3_local_admin_users', JSON.stringify(data.users));
        showNotice(`Deleted user account ${userObj.email}.`);
        return;
      }
    } catch (_e) {}

    setUsersList((prev) => {
      const next = prev.filter((u) => Number(u.id) !== Number(userObj.id));
      localStorage.setItem('best3_local_admin_users', JSON.stringify(next));
      return next;
    });
    showNotice(`Deleted user account ${userObj.email}.`);
  };

  // Discounts & Promotions Helpers and Handlers
  const syncDiscountsState = (nextDiscounts) => {
    setDiscountsList(nextDiscounts);
    if (setContextDiscounts) setContextDiscounts(nextDiscounts);
    localStorage.setItem('best3_local_admin_discounts', JSON.stringify(nextDiscounts));
  };

  const filteredDiscounts = useMemo(() => {
    return discountsList.filter((d) => {
      const matchesType =
        discountTypeFilter === 'all' || d.discount_type === discountTypeFilter;
      const q = discountSearch.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (d.title && d.title.toLowerCase().includes(q)) ||
        (d.code && d.code.toLowerCase().includes(q)) ||
        (d.discount_type && d.discount_type.toLowerCase().includes(q));
      return matchesType && matchesSearch;
    });
  }, [discountsList, discountTypeFilter, discountSearch]);

  const toDateInputValue = (dateStr) => {
    if (!dateStr) return '';
    const normalized = String(dateStr).replace(' ', 'T');
    return normalized.slice(0, 16);
  };

  const getDefaultDateWindow = (daysAhead = 14) => {
    const now = new Date();
    const start = new Date(now.getTime() - 60 * 60 * 1000);
    const end = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);
    const fmt = (dt) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(
        dt.getHours()
      )}:${pad(dt.getMinutes())}`;
    };
    return { starts_at: fmt(start), ends_at: fmt(end) };
  };

  const openCreateDiscountModal = (presetType = 'percentage') => {
    const firstProdId = adminProducts[0]?.id ? [Number(adminProducts[0].id)] : [];
    const window14 = getDefaultDateWindow(14);
    const window30 = getDefaultDateWindow(30);

    if (presetType === 'percentage') {
      setDiscountForm({
        ...EMPTY_DISCOUNT_FORM,
        title: 'Selected Product Time-Limited % Sale',
        discount_type: 'percentage',
        trigger_type: 'automatic',
        code: '',
        discount_value: '15',
        applies_to: 'selected_products',
        product_ids: firstProdId,
        min_order_amount: '0',
        no_time_limit: false,
        starts_at: window14.starts_at,
        ends_at: window14.ends_at,
        max_uses: '',
        is_active: true
      });
    } else if (presetType === 'fixed_amount') {
      setDiscountForm({
        ...EMPTY_DISCOUNT_FORM,
        title: 'Fixed $ Discount (Time Window & Max Uses)',
        discount_type: 'fixed_amount',
        trigger_type: 'code',
        code: 'TENNIS10',
        discount_value: '10.00',
        applies_to: 'all',
        product_ids: [],
        min_order_amount: '0',
        no_time_limit: false,
        starts_at: window30.starts_at,
        ends_at: window30.ends_at,
        max_uses: '25',
        is_active: true
      });
    } else if (presetType === 'free_shipping_product') {
      setDiscountForm({
        ...EMPTY_DISCOUNT_FORM,
        title: 'Permanent Free Shipping on Selected Products',
        discount_type: 'free_shipping_product',
        trigger_type: 'automatic',
        code: '',
        discount_value: '0',
        applies_to: 'selected_products',
        product_ids: firstProdId,
        min_order_amount: '0',
        no_time_limit: true,
        starts_at: '',
        ends_at: '',
        max_uses: '',
        is_active: true
      });
    } else if (presetType === 'free_shipping_code') {
      setDiscountForm({
        ...EMPTY_DISCOUNT_FORM,
        title: 'Promo Code Free Shipping (Dates & Limited Uses)',
        discount_type: 'free_shipping_code',
        trigger_type: 'code',
        code: 'FREESHIP25',
        discount_value: '0',
        applies_to: 'all',
        product_ids: [],
        min_order_amount: '0',
        no_time_limit: false,
        starts_at: window30.starts_at,
        ends_at: window30.ends_at,
        max_uses: '50',
        is_active: true
      });
    } else {
      setDiscountForm({
        ...EMPTY_DISCOUNT_FORM,
        starts_at: window14.starts_at,
        ends_at: window14.ends_at
      });
    }
    setIsDiscountModalOpen(true);
  };

  const openEditDiscountModal = (disc) => {
    setDiscountForm({
      id: disc.id,
      title: disc.title || '',
      discount_type: disc.discount_type || 'percentage',
      trigger_type:
        disc.discount_type === 'free_shipping_code'
          ? 'code'
          : disc.discount_type === 'free_shipping_product'
          ? 'automatic'
          : disc.trigger_type || 'automatic',
      code: disc.code || '',
      discount_value: String(disc.discount_value ?? '0'),
      applies_to: disc.applies_to || 'all',
      product_ids: Array.isArray(disc.product_ids) ? disc.product_ids.map(Number) : [],
      min_order_amount: String(disc.min_order_amount ?? '0'),
      no_time_limit: Boolean(disc.no_time_limit),
      starts_at: toDateInputValue(disc.starts_at),
      ends_at: toDateInputValue(disc.ends_at),
      max_uses: disc.max_uses !== null && disc.max_uses !== undefined ? String(disc.max_uses) : '',
      is_active: disc.is_active !== false
    });
    setIsDiscountModalOpen(true);
  };

  const handleToggleDiscountProduct = (productId) => {
    const pid = Number(productId);
    setDiscountForm((prev) => {
      const current = Array.isArray(prev.product_ids) ? prev.product_ids.map(Number) : [];
      const next = current.includes(pid)
        ? current.filter((id) => id !== pid)
        : [...current, pid];
      return { ...prev, product_ids: next };
    });
  };

  const handleSaveDiscountForm = async (e) => {
    e.preventDefault();
    if (!discountForm.title.trim()) {
      showNotice('Please enter a promotion title.', 'error');
      return;
    }

    const dtype = discountForm.discount_type;
    let triggerType = discountForm.trigger_type;
    if (dtype === 'free_shipping_code') triggerType = 'code';
    if (dtype === 'free_shipping_product') triggerType = 'automatic';

    const cleanCode =
      triggerType === 'code'
        ? String(discountForm.code || '')
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9_-]/g, '')
        : '';

    if (triggerType === 'code' && !cleanCode) {
      showNotice('A promo code is required for code-based discounts.', 'error');
      return;
    }

    const numVal =
      dtype === 'free_shipping_product' || dtype === 'free_shipping_code'
        ? 0
        : Number(discountForm.discount_value || 0);

    if ((dtype === 'percentage' || dtype === 'fixed_amount') && numVal <= 0) {
      showNotice('Discount value must be greater than 0.', 'error');
      return;
    }
    if (dtype === 'percentage' && numVal > 100) {
      showNotice('Percentage discount cannot exceed 100%.', 'error');
      return;
    }

    const appliesTo =
      dtype === 'free_shipping_product' ? 'selected_products' : discountForm.applies_to;
    const prodIds =
      appliesTo === 'selected_products'
        ? (discountForm.product_ids || []).map(Number).filter((n) => n > 0)
        : [];

    if (appliesTo === 'selected_products' && prodIds.length === 0) {
      showNotice('Please select at least one target product for this discount.', 'error');
      return;
    }

    const noTimeLimit = dtype === 'free_shipping_product' ? true : Boolean(discountForm.no_time_limit);
    const maxUsesVal =
      String(discountForm.max_uses).trim() !== '' && Number(discountForm.max_uses) > 0
        ? Number(discountForm.max_uses)
        : null;

    const payload = {
      action: discountForm.id ? 'update_discount' : 'create_discount',
      id: discountForm.id,
      title: discountForm.title.trim(),
      name: discountForm.title.trim(),
      discount_type: dtype,
      trigger_type: triggerType,
      code: cleanCode || null,
      discount_value: numVal,
      applies_to: appliesTo,
      product_ids: prodIds,
      min_order_amount: Number(discountForm.min_order_amount || 0),
      no_time_limit: noTimeLimit,
      starts_at: noTimeLimit || !discountForm.starts_at ? null : discountForm.starts_at,
      ends_at: noTimeLimit || !discountForm.ends_at ? null : discountForm.ends_at,
      max_uses: maxUsesVal,
      is_active: Boolean(discountForm.is_active)
    };

    let updatedFromApi = null;
    try {
      const res = await fetch('/best3intennis-api/api/admin_discounts.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.discounts)) {
        updatedFromApi = data.discounts;
      } else if (data && data.message && !res.ok && res.status !== 404) {
        showNotice(data.message, 'error');
        return;
      }
    } catch (_e) {}

    if (updatedFromApi) {
      syncDiscountsState(updatedFromApi);
    } else {
      const assignedId = discountForm.id || Date.now();
      const existingRow = discountsList.find((d) => Number(d.id) === Number(assignedId));
      const newDiscountObj = {
        id: assignedId,
        title: payload.title,
        discount_type: payload.discount_type,
        trigger_type: payload.trigger_type,
        code: payload.code,
        discount_value: payload.discount_value,
        applies_to: payload.applies_to,
        product_ids: payload.product_ids,
        min_order_amount: payload.min_order_amount,
        no_time_limit: payload.no_time_limit,
        starts_at: payload.starts_at,
        ends_at: payload.ends_at,
        max_uses: payload.max_uses,
        used_count: existingRow ? Number(existingRow.used_count || 0) : 0,
        is_active: payload.is_active,
        is_currently_valid: payload.is_active,
        status_label: payload.is_active ? 'active' : 'disabled'
      };
      const exists = discountsList.some((d) => Number(d.id) === Number(assignedId));
      const nextList = exists
        ? discountsList.map((d) => (Number(d.id) === Number(assignedId) ? newDiscountObj : d))
        : [newDiscountObj, ...discountsList];
      syncDiscountsState(nextList);
    }

    setIsDiscountModalOpen(false);
    showNotice(
      discountForm.id
        ? `Updated discount rule "${payload.title}".`
        : `Created discount rule "${payload.title}".`
    );
  };

  const handleToggleDiscountActive = async (disc) => {
    const nextActive = !disc.is_active;
    try {
      const res = await fetch('/best3intennis-api/api/admin_discounts.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'toggle_discount',
          id: disc.id,
          is_active: nextActive
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.discounts)) {
        syncDiscountsState(data.discounts);
        showNotice(`Discount "${disc.title}" ${nextActive ? 'enabled' : 'paused'}.`);
        return;
      }
    } catch (_e) {}

    const nextList = discountsList.map((d) =>
      Number(d.id) === Number(disc.id)
        ? { ...d, is_active: nextActive, status_label: nextActive ? 'active' : 'disabled' }
        : d
    );
    syncDiscountsState(nextList);
    showNotice(`Discount "${disc.title}" ${nextActive ? 'enabled' : 'paused'}.`);
  };

  const handleResetDiscountUsage = async (disc) => {
    try {
      const res = await fetch('/best3intennis-api/api/admin_discounts.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'reset_usage',
          id: disc.id
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.discounts)) {
        syncDiscountsState(data.discounts);
        showNotice(`Reset usage counter for "${disc.title}".`);
        return;
      }
    } catch (_e) {}

    const nextList = discountsList.map((d) =>
      Number(d.id) === Number(disc.id) ? { ...d, used_count: 0 } : d
    );
    syncDiscountsState(nextList);
    showNotice(`Reset usage counter for "${disc.title}".`);
  };

  const handleDeleteDiscount = async (disc) => {
    try {
      const res = await fetch('/best3intennis-api/api/admin_discounts.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete_discount',
          id: disc.id
        })
      });
      const data = safeParseJson(await res.text());
      if (res.ok && data && data.success && Array.isArray(data.discounts)) {
        syncDiscountsState(data.discounts);
        showNotice(`Deleted discount "${disc.title}".`);
        return;
      }
    } catch (_e) {}

    const nextList = discountsList.filter((d) => Number(d.id) !== Number(disc.id));
    syncDiscountsState(nextList);
    showNotice(`Deleted discount "${disc.title}".`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Admin Header */}
      <header className="bg-slate-900 text-white shadow-lg border-b border-slate-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600/30 border border-indigo-400/40 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-lg font-extrabold tracking-tight text-white">
                Best 3 in Tennis — Admin Backoffice
              </h1>
              <p className="text-xs text-slate-400">
                Store Currency: <strong className="text-amber-300">{storeSettings.default_currency}</strong> • Default Tax:{' '}
                <strong className="text-amber-300">
                  {storeSettings.default_tax_name} ({storeSettings.default_tax_rate}%)
                </strong>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={loadAdminData}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold inline-flex items-center gap-1.5 border border-slate-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <Link
              to="/extras-shop"
              className="px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold inline-flex items-center gap-1.5 shadow"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View Live Shop
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="px-3 py-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30 text-xs font-semibold inline-flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {/* Status Banner */}
        {bannerMessage && (
          <div
            role="status"
            className={`mb-6 p-4 rounded-xl border flex items-center justify-between shadow-sm ${
              bannerMessage.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-emerald-50 border-emerald-200 text-emerald-800'
            }`}
          >
            <div className="flex items-center gap-2.5 text-sm font-semibold">
              {bannerMessage.type === 'error' ? (
                <AlertCircle className="w-5 h-5 text-red-600" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              )}
              <span>{bannerMessage.text}</span>
            </div>
            <button
              type="button"
              onClick={() => setBannerMessage(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                Total Store Revenue
              </p>
              <p className="text-2xl font-extrabold text-gray-900 mt-1">
                {formatPrice(kpiStats.totalRevenue, storeSettings.default_currency)}
              </p>
              <p className="text-xs text-emerald-600 font-medium mt-1">
                {kpiStats.paidCount} paid orders
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                Pending Fulfillment
              </p>
              <p className="text-2xl font-extrabold text-gray-900 mt-1">
                {kpiStats.pendingFulfillment}
              </p>
              <p className="text-xs text-amber-600 font-medium mt-1">
                {kpiStats.totalOrders} total orders tracked
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                Active Products
              </p>
              <p className="text-2xl font-extrabold text-gray-900 mt-1">
                {kpiStats.activeProducts} / {adminProducts.length}
              </p>
              <p className="text-xs text-indigo-600 font-medium mt-1">
                {kpiStats.lowStockCount} low-stock alerts
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                Tax &amp; Currency
              </p>
              <p className="text-2xl font-extrabold text-gray-900 mt-1">
                {storeSettings.default_currency} • {storeSettings.default_tax_rate}%
              </p>
              <p className="text-xs text-purple-600 font-medium mt-1">
                {taxZones.length} regional tax zones active
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <Globe className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('products')}
            data-testid="admin-tab-products"
            className={`px-5 py-3 font-bold text-sm rounded-t-xl inline-flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'products'
                ? 'border-indigo-600 text-indigo-700 bg-white'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Package className="w-4 h-4" />
            Products Catalog ({adminProducts.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('orders')}
            data-testid="admin-tab-orders"
            className={`px-5 py-3 font-bold text-sm rounded-t-xl inline-flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'orders'
                ? 'border-indigo-600 text-indigo-700 bg-white'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Truck className="w-4 h-4" />
            Order Tracking &amp; Fulfillment ({orders.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('settings')}
            data-testid="admin-tab-settings"
            className={`px-5 py-3 font-bold text-sm rounded-t-xl inline-flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'settings'
                ? 'border-indigo-600 text-indigo-700 bg-white'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Globe className="w-4 h-4" />
            Currency, Shipping &amp; Tax Zones ({taxZones.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('paypal')}
            data-testid="admin-tab-paypal"
            className={`px-5 py-3 font-bold text-sm rounded-t-xl inline-flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'paypal'
                ? 'border-indigo-600 text-indigo-700 bg-white'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            PayPal Configuration ({(storeSettings.paypal_mode || 'sandbox').toUpperCase()})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('users')}
            data-testid="admin-tab-users"
            className={`px-5 py-3 font-bold text-sm rounded-t-xl inline-flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'users'
                ? 'border-indigo-600 text-indigo-700 bg-white'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4" />
            User Management ({usersList.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('discounts')}
            data-testid="admin-tab-discounts"
            className={`px-5 py-3 font-bold text-sm rounded-t-xl inline-flex items-center gap-2 border-b-2 transition-all ${
              activeTab === 'discounts'
                ? 'border-indigo-600 text-indigo-700 bg-white'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Tag className="w-4 h-4" />
            Discounts &amp; Promos ({discountsList.length})
          </button>
        </div>

        {/* TAB 1: PRODUCTS MANAGEMENT */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap items-center gap-3 flex-1">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search by product name, SKU, or category..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <select
                  value={productCategoryFilter}
                  onChange={(e) => setProductCategoryFilter(e.target.value)}
                  aria-label="Filter products by category"
                  className="px-3.5 py-2 border border-gray-300 rounded-xl text-sm bg-white font-medium text-gray-700"
                >
                  <option value="All">All Categories</option>
                  <option value="Books & Journals">Books &amp; Journals</option>
                  <option value="Bundles & Kits">Bundles &amp; Kits</option>
                  <option value="Accessories">Accessories</option>
                  <option value="Posters & Guides">Posters &amp; Guides</option>
                  <option value="Gift Cards">Gift Cards</option>
                </select>
              </div>

              <button
                type="button"
                onClick={openCreateProductModal}
                data-testid="admin-add-product-btn"
                className="px-4 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm rounded-xl shadow inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add New Product
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-400">
                    <th className="py-3 px-3">Product</th>
                    <th className="py-3 px-3">SKU &amp; Type</th>
                    <th className="py-3 px-3">Category</th>
                    <th className="py-3 px-3">Price</th>
                    <th className="py-3 px-3">Inventory</th>
                    <th className="py-3 px-3">Status</th>
                    <th className="py-3 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredProducts.map((prod) => (
                    <tr key={prod.id} className="hover:bg-gray-50/80">
                      <td className="py-3.5 px-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={prod.primary_image_url || '/Images/cover.jpg'}
                            alt={prod.name}
                            className="w-12 h-12 rounded-xl object-contain bg-gray-100 border border-gray-200 p-1 flex-shrink-0"
                          />
                          <div>
                            <div className="font-bold text-gray-900 flex items-center gap-1.5">
                              {prod.name}
                              {prod.is_featured && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                                  <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                                  Featured
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-1 max-w-xs">
                              {prod.short_description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-3">
                        <div className="font-mono text-xs font-semibold text-gray-700">
                          {prod.sku}
                        </div>
                        <span className="inline-block mt-0.5 text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">
                          {prod.product_type || 'physical'}
                        </span>
                      </td>
                      <td className="py-3.5 px-3 text-gray-600 font-medium">{prod.category}</td>
                      <td className="py-3.5 px-3">
                        <div className="font-extrabold text-gray-900">
                          ${Number(prod.price || 0).toFixed(2)}{' '}
                          <span className="text-xs font-normal text-gray-500">
                            {prod.currency || storeSettings.default_currency}
                          </span>
                        </div>
                        {prod.compare_at_price && (
                          <div className="text-xs text-gray-400 line-through">
                            ${Number(prod.compare_at_price).toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="py-3.5 px-3">
                        {prod.track_inventory ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                              Number(prod.stock_quantity) > 5
                                ? 'bg-emerald-50 text-emerald-700'
                                : Number(prod.stock_quantity) > 0
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-red-50 text-red-700'
                            }`}
                          >
                            {prod.stock_quantity} in stock
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">Unlimited (Digital)</span>
                        )}
                      </td>
                      <td className="py-3.5 px-3">
                        <button
                          type="button"
                          onClick={() => handleToggleProductField(prod, 'is_active')}
                          className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            prod.is_active
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {prod.is_active ? 'Active' : 'Hidden'}
                        </button>
                      </td>
                      <td className="py-3.5 px-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEditProductModal(prod)}
                            data-testid={`edit-product-${prod.sku}`}
                            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50"
                            title="Edit Product"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteProduct(prod)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                            title="Delete Product"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 2: ORDER TRACKING & FULFILLMENT */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search by Order # (B3T-...), customer name, or email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {['all', 'unfulfilled', 'processing', 'shipped', 'delivered', 'cancelled'].map(
                  (status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setOrderStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold capitalize transition-all ${
                        orderStatusFilter === status
                          ? 'bg-indigo-700 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {status}
                    </button>
                  )
                )}
              </div>
            </div>

            {filteredOrders.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-2xl">
                <Truck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-700 font-bold">No matching orders found</p>
                <p className="text-xs text-gray-500 mt-1">
                  Orders placed via PayPal Checkout will automatically appear here for tracking and fulfillment.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-400">
                      <th className="py-3 px-3">Order #</th>
                      <th className="py-3 px-3">Customer</th>
                      <th className="py-3 px-3">Total &amp; Tax</th>
                      <th className="py-3 px-3">Payment</th>
                      <th className="py-3 px-3">Fulfillment</th>
                      <th className="py-3 px-3">Tracking</th>
                      <th className="py-3 px-3 text-right">Manage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {filteredOrders.map((ord) => (
                      <tr key={ord.order_number} className="hover:bg-gray-50">
                        <td className="py-3.5 px-3 font-mono font-bold text-indigo-700">
                          {ord.order_number}
                          <div className="text-[11px] font-normal text-gray-400">
                            {ord.created_at
                              ? new Date(ord.created_at).toLocaleDateString()
                              : 'Today'}
                          </div>
                        </td>
                        <td className="py-3.5 px-3">
                          <div className="font-bold text-gray-900">{ord.customer_name}</div>
                          <div className="text-xs text-gray-500">{ord.customer_email}</div>
                        </td>
                        <td className="py-3.5 px-3">
                          <div className="font-extrabold text-gray-900">
                            ${Number(ord.total_amount || 0).toFixed(2)}{' '}
                            {ord.currency || storeSettings.default_currency}
                          </div>
                          <div className="text-xs text-gray-500">
                            Tax ({ord.tax_label || 'HST'}): ${Number(ord.tax_amount || 0).toFixed(2)}
                          </div>
                        </td>
                        <td className="py-3.5 px-3">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 uppercase">
                            {ord.payment_status || 'paid'}
                          </span>
                        </td>
                        <td className="py-3.5 px-3">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${
                              ord.fulfillment_status === 'delivered'
                                ? 'bg-emerald-100 text-emerald-800'
                                : ord.fulfillment_status === 'shipped'
                                ? 'bg-blue-100 text-blue-800'
                                : ord.fulfillment_status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {ord.fulfillment_status || 'processing'}
                          </span>
                        </td>
                        <td className="py-3.5 px-3 text-xs text-gray-600">
                          {ord.tracking_number ? (
                            <div>
                              <span className="font-bold">{ord.tracking_carrier}:</span>{' '}
                              <span className="font-mono">{ord.tracking_number}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">Not assigned</span>
                          )}
                        </td>
                        <td className="py-3.5 px-3 text-right">
                          <button
                            type="button"
                            onClick={() => openOrderModal(ord)}
                            data-testid={`manage-order-${ord.order_number}`}
                            className="px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs"
                          >
                            Update / Track
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: CURRENCY, SHIPPING & TAX ZONES */}
        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Store Currency & Shipping Form */}
            <div className="lg:col-span-5 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2.5 mb-4">
                <DollarSign className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-extrabold text-gray-900">
                  Store Currency &amp; Shipping Defaults
                </h2>
              </div>
              <p className="text-xs text-gray-500 mb-6">
                Configure the store&apos;s primary currency (default <strong>CAD</strong>), default tax label/rate (default <strong>HST 13%</strong>), and shipping rules.
              </p>

              <form onSubmit={handleSaveStoreSettings} className="space-y-4">
                <div>
                  <label
                    htmlFor="store-default-currency"
                    className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                  >
                    Default Store Currency
                  </label>
                  <select
                    id="store-default-currency"
                    value={storeSettings.default_currency || 'CAD'}
                    onChange={(e) =>
                      setStoreSettings((prev) => ({ ...prev, default_currency: e.target.value }))
                    }
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-bold text-gray-800 bg-white"
                  >
                    <option value="CAD">CAD — Canadian Dollar ($ CAD) [Default]</option>
                    <option value="USD">USD — US Dollar ($ USD)</option>
                    <option value="EUR">EUR — Euro (€ EUR)</option>
                    <option value="GBP">GBP — British Pound (£ GBP)</option>
                    <option value="AUD">AUD — Australian Dollar ($ AUD)</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="store-default-tax-name"
                      className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                    >
                      Default Tax Label
                    </label>
                    <input
                      id="store-default-tax-name"
                      type="text"
                      value={storeSettings.default_tax_name || 'HST'}
                      onChange={(e) =>
                        setStoreSettings((prev) => ({ ...prev, default_tax_name: e.target.value }))
                      }
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="store-default-tax-rate"
                      className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                    >
                      Default Tax Rate (%)
                    </label>
                    <input
                      id="store-default-tax-rate"
                      type="number"
                      step="0.001"
                      value={storeSettings.default_tax_rate ?? 13}
                      onChange={(e) =>
                        setStoreSettings((prev) => ({ ...prev, default_tax_rate: e.target.value }))
                      }
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="store-flat-shipping"
                      className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                    >
                      Flat Shipping ({storeSettings.default_currency})
                    </label>
                    <input
                      id="store-flat-shipping"
                      type="number"
                      step="0.01"
                      value={storeSettings.flat_shipping_rate ?? 5.99}
                      onChange={(e) =>
                        setStoreSettings((prev) => ({
                          ...prev,
                          flat_shipping_rate: e.target.value
                        }))
                      }
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="store-free-shipping"
                      className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                    >
                      Free Shipping Threshold
                    </label>
                    <input
                      id="store-free-shipping"
                      type="number"
                      step="0.01"
                      value={storeSettings.free_shipping_threshold ?? 50.0}
                      onChange={(e) =>
                        setStoreSettings((prev) => ({
                          ...prev,
                          free_shipping_threshold: e.target.value
                        }))
                      }
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="store-notice-input"
                    className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                  >
                    Storefront Promo Notice
                  </label>
                  <input
                    id="store-notice-input"
                    type="text"
                    value={storeSettings.store_notice || ''}
                    onChange={(e) =>
                      setStoreSettings((prev) => ({ ...prev, store_notice: e.target.value }))
                    }
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="save-store-settings-btn"
                  className="w-full py-2.5 px-4 bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm rounded-xl shadow inline-flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Currency &amp; Shipping Settings
                </button>
              </form>
            </div>

            {/* Regional Tax Zones Manager */}
            <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Percent className="w-5 h-5 text-indigo-600" />
                    <h2 className="text-lg font-extrabold text-gray-900">
                      Regional Tax Zones (Default: Ontario HST 13%)
                    </h2>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Configure tax rates per country and province/state for Checkout &amp; PayPal calculations.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={openCreateTaxZoneModal}
                  data-testid="add-tax-zone-btn"
                  className="px-3.5 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-xs rounded-xl shadow inline-flex items-center gap-1.5 self-start"
                >
                  <Plus className="w-4 h-4" />
                  Add Tax Zone
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-400">
                      <th className="py-2.5 px-3">Zone Name</th>
                      <th className="py-2.5 px-3">Country / Region</th>
                      <th className="py-2.5 px-3">Tax Name &amp; Rate</th>
                      <th className="py-2.5 px-3">Default</th>
                      <th className="py-2.5 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {taxZones.map((zone) => (
                      <tr key={zone.id} className="hover:bg-gray-50">
                        <td className="py-3 px-3 font-bold text-gray-900">{zone.name}</td>
                        <td className="py-3 px-3">
                          <span className="inline-flex items-center gap-1 font-mono text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-700">
                            <MapPin className="w-3 h-3 text-indigo-500" />
                            {zone.country_code} / {zone.region_code || '*'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="font-extrabold text-indigo-700">
                            {zone.tax_name} ({Number(zone.tax_rate).toFixed(3).replace(/\.?0+$/, '')}%)
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          {zone.is_default ? (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                              Default Zone
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSetDefaultTaxZone(zone)}
                              className="text-xs font-semibold text-indigo-600 hover:underline"
                            >
                              Make Default
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEditTaxZoneModal(zone)}
                              className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50"
                              title="Edit Tax Zone"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            {!zone.is_default && (
                              <button
                                type="button"
                                onClick={() => handleDeleteTaxZone(zone)}
                                className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                                title="Delete Tax Zone"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: PAYPAL CONFIGURATION */}
        {activeTab === 'paypal' && (
          <div className="space-y-6" data-testid="admin-paypal-config-section">
            {/* PayPal Header & Refresh Bar */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#003087] text-white flex items-center justify-center font-black italic text-lg flex-shrink-0 shadow">
                  PP
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h2 className="text-xl font-extrabold text-gray-900">
                      PayPal Checkout Configuration
                    </h2>
                    <span
                      data-testid="paypal-status-badge"
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        storeSettings.paypal_enabled !== false
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {storeSettings.paypal_enabled !== false ? 'Active / Enabled' : 'Disabled'}
                    </span>
                    <span
                      data-testid="paypal-mode-badge"
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                        storeSettings.paypal_mode === 'live'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      Mode: {storeSettings.paypal_mode === 'live' ? 'Live (Production)' : 'Sandbox (Testing)'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Manage PayPal Express Checkout environment mode, merchant branding, payment funding options, and settlement currency.
                  </p>
                  {paypalStatus.refreshed_at && (
                    <p className="text-[11px] text-gray-400 mt-1" data-testid="paypal-last-refreshed">
                      Last synced: {new Date(paypalStatus.refreshed_at).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 self-start md:self-center">
                <button
                  type="button"
                  onClick={handleRefreshPaypalConfig}
                  disabled={refreshingPaypal}
                  data-testid="refresh-paypal-config-btn"
                  className="px-4 py-2.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs inline-flex items-center gap-2 border border-indigo-200 transition cursor-pointer disabled:opacity-60"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshingPaypal ? 'animate-spin' : ''}`} />
                  {refreshingPaypal ? 'Refreshing...' : 'Refresh Configuration'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column: PayPal Settings Form */}
              <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3 mb-5">
                  Gateway &amp; Checkout Settings
                </h3>

                <form onSubmit={handleSavePaypalConfig} className="space-y-5">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <div>
                      <label
                        htmlFor="paypal-enabled-toggle"
                        className="text-sm font-bold text-gray-900 block cursor-pointer"
                      >
                        Enable PayPal Checkout on Storefront
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Allow customers to complete orders via PayPal Express Checkout.
                      </p>
                    </div>
                    <input
                      id="paypal-enabled-toggle"
                      type="checkbox"
                      checked={storeSettings.paypal_enabled !== false}
                      onChange={(e) =>
                        setStoreSettings((prev) => ({
                          ...prev,
                          paypal_enabled: e.target.checked
                        }))
                      }
                      data-testid="paypal-enabled-checkbox"
                      className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="paypal-mode-select"
                        className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                      >
                        PayPal Environment Mode
                      </label>
                      <select
                        id="paypal-mode-select"
                        value={storeSettings.paypal_mode || 'sandbox'}
                        onChange={(e) =>
                          setStoreSettings((prev) => ({
                            ...prev,
                            paypal_mode: e.target.value
                          }))
                        }
                        data-testid="paypal-mode-select"
                        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-bold text-gray-800 bg-white"
                      >
                        <option value="sandbox">Sandbox (Testing Environment)</option>
                        <option value="live">Live (Production Transactions)</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="paypal-settlement-currency"
                        className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                      >
                        Settlement Currency
                      </label>
                      <select
                        id="paypal-settlement-currency"
                        value={storeSettings.default_currency || 'CAD'}
                        onChange={(e) =>
                          setStoreSettings((prev) => ({
                            ...prev,
                            default_currency: e.target.value
                          }))
                        }
                        data-testid="paypal-currency-select"
                        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-bold text-gray-800 bg-white"
                      >
                        <option value="CAD">CAD — Canadian Dollar ($ CAD)</option>
                        <option value="USD">USD — US Dollar ($ USD)</option>
                        <option value="EUR">EUR — Euro (€ EUR)</option>
                        <option value="GBP">GBP — British Pound (£ GBP)</option>
                        <option value="AUD">AUD — Australian Dollar ($ AUD)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="paypal-merchant-name"
                      className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                    >
                      Merchant / Brand Display Name
                    </label>
                    <input
                      id="paypal-merchant-name"
                      type="text"
                      value={storeSettings.paypal_merchant_name ?? 'Best 3 in Tennis Pro Shop'}
                      onChange={(e) =>
                        setStoreSettings((prev) => ({
                          ...prev,
                          paypal_merchant_name: e.target.value
                        }))
                      }
                      placeholder="Best 3 in Tennis Pro Shop"
                      data-testid="paypal-merchant-name-input"
                      className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Displayed to customers in the PayPal approval window and order receipts.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="paypal-support-email"
                        className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                      >
                        Customer Support Email
                      </label>
                      <input
                        id="paypal-support-email"
                        type="email"
                        value={storeSettings.paypal_support_email ?? 'support@best3intennis.com'}
                        onChange={(e) =>
                          setStoreSettings((prev) => ({
                            ...prev,
                            paypal_support_email: e.target.value
                          }))
                        }
                        placeholder="support@best3intennis.com"
                        data-testid="paypal-support-email-input"
                        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Shown to buyers on checkout receipts &amp; confirmation emails. Customers reply here for support.
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="paypal-order-notification-email"
                        className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                      >
                        Order Notification Email
                      </label>
                      <input
                        id="paypal-order-notification-email"
                        type="email"
                        value={
                          storeSettings.paypal_order_notification_email ??
                          storeSettings.paypal_merchant_email ??
                          'dimabot@gmail.com'
                        }
                        onChange={(e) =>
                          setStoreSettings((prev) => ({
                            ...prev,
                            paypal_order_notification_email: e.target.value,
                            paypal_merchant_email: e.target.value
                          }))
                        }
                        placeholder="dimabot@gmail.com"
                        data-testid="paypal-order-notification-email-input"
                        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Instant staff alert sent to this address on every newly paid order with items &amp; shipping details.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <div>
                      <label
                        htmlFor="paypal-card-funding-toggle"
                        className="text-sm font-bold text-gray-900 block cursor-pointer"
                      >
                        Allow Debit &amp; Credit Card Guest Funding
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Enable customers to pay with Visa, Mastercard, or Amex through PayPal without needing a PayPal account.
                      </p>
                    </div>
                    <input
                      id="paypal-card-funding-toggle"
                      type="checkbox"
                      checked={storeSettings.paypal_allow_card_funding !== false}
                      onChange={(e) =>
                        setStoreSettings((prev) => ({
                          ...prev,
                          paypal_allow_card_funding: e.target.checked
                        }))
                      }
                      data-testid="paypal-card-funding-checkbox"
                      className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                      type="submit"
                      data-testid="save-paypal-settings-btn"
                      className="flex-1 py-2.5 px-5 bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm rounded-xl shadow inline-flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Save className="w-4 h-4" />
                      Save PayPal Configuration
                    </button>
                    <button
                      type="button"
                      onClick={handleRefreshPaypalConfig}
                      disabled={refreshingPaypal}
                      className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm rounded-xl inline-flex items-center gap-2 cursor-pointer"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingPaypal ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </form>
              </div>

              {/* Right Column: Active PayPal Summary & Checkout Preview */}
              <div className="lg:col-span-5 space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3 mb-4">
                    Active Payment Gateway Summary
                  </h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Checkout Status</dt>
                      <dd className="font-bold text-gray-900">
                        {storeSettings.paypal_enabled !== false ? 'Enabled' : 'Disabled'}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Environment Mode</dt>
                      <dd className="font-bold uppercase text-indigo-700">
                        {storeSettings.paypal_mode || 'sandbox'}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Payment Action</dt>
                      <dd className="font-bold text-gray-900">Immediate Capture (Orders v2)</dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Settlement Currency</dt>
                      <dd className="font-bold text-gray-900">
                        {storeSettings.default_currency || 'CAD'}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Default Tax Rule</dt>
                      <dd className="font-bold text-gray-900">
                        {storeSettings.default_tax_name || 'HST'} ({storeSettings.default_tax_rate ?? 13}%)
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Merchant Display Name</dt>
                      <dd className="font-bold text-gray-900">
                        {storeSettings.paypal_merchant_name || 'Best 3 in Tennis Pro Shop'}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Customer Support</dt>
                      <dd
                        className="font-bold text-gray-900 truncate max-w-[190px]"
                        title={storeSettings.paypal_support_email || 'support@best3intennis.com'}
                        data-testid="summary-support-email"
                      >
                        {storeSettings.paypal_support_email || 'support@best3intennis.com'}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                      <dt className="text-gray-500 font-medium">Order Alert Email</dt>
                      <dd
                        className="font-bold text-gray-900 truncate max-w-[190px]"
                        title={
                          storeSettings.paypal_order_notification_email ||
                          storeSettings.paypal_merchant_email ||
                          'dimabot@gmail.com'
                        }
                        data-testid="summary-order-notification-email"
                      >
                        {storeSettings.paypal_order_notification_email ||
                          storeSettings.paypal_merchant_email ||
                          'dimabot@gmail.com'}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-1.5">
                      <dt className="text-gray-500 font-medium">Card Guest Funding</dt>
                      <dd className="font-bold text-emerald-700">
                        {storeSettings.paypal_allow_card_funding !== false ? 'Allowed' : 'PayPal Balance Only'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: USER MANAGEMENT (TENNISLOG PLAYERS & ADMIN USERS) */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="admin-users-section">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  TennisLog Players &amp; Admin Users
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Click any TennisLog player or their <strong>Matches Grid</strong> button to inspect all matches logged in an overlay grid. Manage roles, lock/unlock accounts, reset passwords directly, or initiate password reset emails.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => openCreateUserModal('user')}
                  data-testid="admin-add-user-btn"
                  className="px-4 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-xs rounded-xl shadow inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <UserPlus className="w-4 h-4" />
                  Add TennisLog / Admin User
                </button>
              </div>
            </div>

            {/* Filters & Search Bar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { key: 'all', label: `All Users (${usersList.length})`, testId: 'user-filter-all' },
                  {
                    key: 'user',
                    label: `TennisLog Players (${usersList.filter((u) => u.role !== 'admin').length})`,
                    testId: 'user-filter-tennis'
                  },
                  {
                    key: 'admin',
                    label: `Admin Users (${usersList.filter((u) => u.role === 'admin').length})`,
                    testId: 'user-filter-admin'
                  },
                  {
                    key: 'locked',
                    label: `Locked (${usersList.filter((u) => u.is_locked).length})`,
                    testId: 'user-filter-locked'
                  }
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setUserRoleFilter(f.key)}
                    data-testid={f.testId}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      userRoleFilter === f.key
                        ? 'bg-indigo-700 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  data-testid="admin-user-search"
                  placeholder="Search by player name, email, or club..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>

            {/* Users Table */}
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-2xl">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-700 font-bold">No matching users found</p>
                <p className="text-xs text-gray-500 mt-1">
                  Try clearing your search filter or add a new TennisLog or Admin user.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-400">
                      <th className="py-3 px-3">Player / Admin Account</th>
                      <th className="py-3 px-3">Role</th>
                      <th className="py-3 px-3">Club &amp; Ratings</th>
                      <th className="py-3 px-3">Matches Logged</th>
                      <th className="py-3 px-3">Account Status</th>
                      <th className="py-3 px-3 text-right">Security &amp; Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {filteredUsers.map((u) => {
                      const displayName =
                        u.full_name ||
                        `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
                        u.email;
                      const isPrimaryAdmin =
                        u.email && u.email.toLowerCase() === 'dimabot@gmail.com';

                      return (
                        <tr
                          key={u.id}
                          data-testid={`user-row-${u.id}`}
                          className={`hover:bg-indigo-50/40 transition ${
                            u.is_locked ? 'bg-red-50/30' : ''
                          }`}
                        >
                          <td className="py-3.5 px-3">
                            <button
                              type="button"
                              onClick={() => handleOpenUserMatches(u)}
                              data-testid={`user-name-trigger-${u.id}`}
                              className="text-left group cursor-pointer"
                              title="Click to view player's logged matches in grid overlay"
                            >
                              <div className="font-extrabold text-gray-900 group-hover:text-indigo-700 flex items-center gap-1.5">
                                <span className="underline decoration-dotted underline-offset-2">
                                  {displayName}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 font-mono">{u.email}</div>
                              {u.created_at && (
                                <div className="text-[11px] text-gray-400 mt-0.5">
                                  Joined: {new Date(u.created_at).toLocaleDateString()}
                                </div>
                              )}
                            </button>
                          </td>

                          <td className="py-3.5 px-3">
                            {u.role === 'admin' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                                <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
                                Admin User
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                🎾 TennisLog Player
                              </span>
                            )}
                          </td>

                          <td className="py-3.5 px-3">
                            <div className="text-xs font-semibold text-gray-800">
                              {u.club_name || <span className="text-gray-400">No club listed</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {u.utr && (
                                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                                  UTR {u.utr}
                                </span>
                              )}
                              {u.wtn && (
                                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-purple-50 text-purple-700">
                                  WTN {u.wtn}
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="py-3.5 px-3">
                            <button
                              type="button"
                              onClick={() => handleOpenUserMatches(u)}
                              data-testid={`view-user-matches-${u.id}`}
                              className="px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs inline-flex items-center gap-1.5 border border-indigo-200/80 transition cursor-pointer"
                            >
                              <LayoutGrid className="w-3.5 h-3.5" />
                              <span>{u.matches_count || 0} Matches</span>
                              {(u.wins_count > 0 || u.losses_count > 0) && (
                                <span className="text-[11px] font-semibold text-indigo-500">
                                  ({u.wins_count || 0}W - {u.losses_count || 0}L)
                                </span>
                              )}
                            </button>
                          </td>

                          <td className="py-3.5 px-3">
                            <div className="flex flex-col items-start gap-1">
                              {u.is_locked ? (
                                <span
                                  data-testid={`user-status-badge-${u.id}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800"
                                >
                                  <Lock className="w-3 h-3" />
                                  Locked
                                </span>
                              ) : u.is_active ? (
                                <span
                                  data-testid={`user-status-badge-${u.id}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800"
                                >
                                  <CheckCircle2 className="w-3 h-3" />
                                  Active
                                </span>
                              ) : (
                                <span
                                  data-testid={`user-status-badge-${u.id}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800"
                                >
                                  Unverified
                                </span>
                              )}

                              {u.has_pending_reset && (
                                <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                                  Reset Email Sent
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="py-3.5 px-3 text-right">
                            <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => openPasswordResetModal(u)}
                                data-testid={`reset-password-btn-${u.id}`}
                                className="px-2.5 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 font-bold text-xs inline-flex items-center gap-1 cursor-pointer"
                                title="Reset Password Directly or via Email"
                              >
                                <Key className="w-3.5 h-3.5" />
                                Reset Password
                              </button>

                              <button
                                type="button"
                                onClick={() => handleSendPasswordResetEmail(u)}
                                disabled={sendingResetEmailId === u.id}
                                data-testid={`email-reset-btn-${u.id}`}
                                className="px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 font-bold text-xs inline-flex items-center gap-1 cursor-pointer disabled:opacity-50"
                                title="Send Password Reset Link to User Email"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                {sendingResetEmailId === u.id ? 'Sending...' : 'Email Reset'}
                              </button>

                              <button
                                type="button"
                                onClick={() => handleToggleUserLock(u)}
                                data-testid={`toggle-lock-btn-${u.id}`}
                                className={`px-2.5 py-1.5 rounded-lg font-bold text-xs inline-flex items-center gap-1 border cursor-pointer ${
                                  u.is_locked
                                    ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'
                                    : 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                }`}
                                title={u.is_locked ? 'Unlock User Account' : 'Lock User Account'}
                              >
                                {u.is_locked ? (
                                  <>
                                    <Unlock className="w-3.5 h-3.5" />
                                    Unlock
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-3.5 h-3.5" />
                                    Lock
                                  </>
                                )}
                              </button>

                              <button
                                type="button"
                                onClick={() => openEditUserModal(u)}
                                data-testid={`edit-user-btn-${u.id}`}
                                className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 cursor-pointer"
                                title="Edit User / Role"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>

                              {!isPrimaryAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(u)}
                                  data-testid={`delete-user-btn-${u.id}`}
                                  className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer"
                                  title="Delete User"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 6: DISCOUNTS & PROMOTIONS MODEL */}
        {activeTab === 'discounts' && (
          <div className="space-y-6" data-testid="admin-discounts-panel">
            {/* 4 Discount Model Quick-Launch Cards */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
                <div>
                  <h2 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-indigo-600" />
                    Storefront Discount &amp; Free Shipping Models
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Launch targeted product percentage sales, fixed dollar discounts with time or usage caps, permanent product free shipping, or promo-code free shipping with date &amp; usage limits.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openCreateDiscountModal('percentage')}
                  data-testid="create-discount-btn"
                  className="px-4 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl font-bold text-xs inline-flex items-center gap-2 shadow cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  Create Discount / Promo
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  type="button"
                  onClick={() => openCreateDiscountModal('percentage')}
                  data-testid="preset-percentage-btn"
                  className="text-left p-4 rounded-2xl border-2 border-indigo-100 hover:border-indigo-500 bg-indigo-50/40 hover:bg-indigo-50 transition group cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase bg-indigo-600 text-white">
                      1. % Discount
                    </span>
                    <Percent className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="font-extrabold text-sm text-gray-900 group-hover:text-indigo-700">
                    Percentage Off Selected Product
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Percentage discount for a configured period of time on selected product(s).
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => openCreateDiscountModal('fixed_amount')}
                  data-testid="preset-fixed-amount-btn"
                  className="text-left p-4 rounded-2xl border-2 border-emerald-100 hover:border-emerald-500 bg-emerald-50/40 hover:bg-emerald-50 transition group cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase bg-emerald-600 text-white">
                      2. Fixed $ Off
                    </span>
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="font-extrabold text-sm text-gray-900 group-hover:text-emerald-700">
                    Fixed Amount Discount
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Fixed dollar discount for a fixed time window and/or specific number of implementations.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => openCreateDiscountModal('free_shipping_product')}
                  data-testid="preset-free-shipping-product-btn"
                  className="text-left p-4 rounded-2xl border-2 border-teal-100 hover:border-teal-500 bg-teal-50/40 hover:bg-teal-50 transition group cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase bg-teal-600 text-white">
                      3. Product Free Ship
                    </span>
                    <Truck className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="font-extrabold text-sm text-gray-900 group-hover:text-teal-700">
                    Free Shipping (No Time Limit)
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Permanent automatic free shipping for specific products without any time limit.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => openCreateDiscountModal('free_shipping_code')}
                  data-testid="preset-free-shipping-code-btn"
                  className="text-left p-4 rounded-2xl border-2 border-amber-200 hover:border-amber-500 bg-amber-50/50 hover:bg-amber-50 transition group cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase bg-amber-500 text-slate-950">
                      4. Code Free Ship
                    </span>
                    <Tag className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="font-extrabold text-sm text-gray-900 group-hover:text-amber-800">
                    Free Shipping by Code
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Promo code free shipping valid for specific dates and a maximum number of uses.
                  </p>
                </button>
              </div>
            </div>

            {/* Discounts Filter & Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { id: 'all', label: `All Rules (${discountsList.length})` },
                    { id: 'percentage', label: '% Off Product' },
                    { id: 'fixed_amount', label: 'Fixed $ Off' },
                    { id: 'free_shipping_product', label: 'Product Free Shipping' },
                    { id: 'free_shipping_code', label: 'Code Free Shipping' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setDiscountTypeFilter(tab.id)}
                      data-testid={`discount-filter-${tab.id}`}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                        discountTypeFilter === tab.id
                          ? 'bg-indigo-700 text-white shadow-2xs'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="relative w-full md:w-72">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={discountSearch}
                    onChange={(e) => setDiscountSearch(e.target.value)}
                    data-testid="discount-search-input"
                    placeholder="Search title or promo code..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>

              {filteredDiscounts.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-2xl">
                  <Tag className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-700 font-bold">No discount rules configured yet</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4">
                    Select one of the 4 promotion presets above to create your first discount or free shipping rule.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse" data-testid="discounts-table">
                    <thead>
                      <tr className="border-b border-gray-200 text-[11px] font-extrabold uppercase tracking-wider text-gray-400">
                        <th className="py-3 px-3">Promotion &amp; Trigger</th>
                        <th className="py-3 px-3">Model &amp; Benefit</th>
                        <th className="py-3 px-3">Target Scope</th>
                        <th className="py-3 px-3">Time Period</th>
                        <th className="py-3 px-3">Uses / Limit</th>
                        <th className="py-3 px-3">Status</th>
                        <th className="py-3 px-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm">
                      {filteredDiscounts.map((d) => {
                        const selectedProds =
                          d.applies_to === 'selected_products' && Array.isArray(d.product_ids)
                            ? adminProducts.filter((p) =>
                                d.product_ids.map(Number).includes(Number(p.id))
                              )
                            : [];
                        const maxUses =
                          d.max_uses !== null && d.max_uses !== undefined && Number(d.max_uses) > 0
                            ? Number(d.max_uses)
                            : null;
                        const usedCount = Number(d.used_count || 0);
                        const isExhausted = maxUses !== null && usedCount >= maxUses;

                        return (
                          <tr
                            key={d.id}
                            data-testid={`discount-row-${d.id}`}
                            className="hover:bg-gray-50/80 transition"
                          >
                            <td className="py-3.5 px-3">
                              <div className="font-extrabold text-gray-900">{d.title}</div>
                              <div className="mt-1 flex items-center gap-1.5">
                                {d.trigger_type === 'code' || d.discount_type === 'free_shipping_code' ? (
                                  <span
                                    className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 font-mono text-xs font-extrabold"
                                    data-testid={`discount-code-badge-${d.id}`}
                                  >
                                    CODE: {d.code}
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-bold">
                                    ⚡ Automatic
                                  </span>
                                )}
                                {Number(d.min_order_amount || 0) > 0 && (
                                  <span className="text-[11px] text-gray-500">
                                    Min ${Number(d.min_order_amount).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="py-3.5 px-3">
                              {d.discount_type === 'percentage' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold bg-indigo-100 text-indigo-800">
                                  {Number(d.discount_value)}% OFF
                                </span>
                              )}
                              {d.discount_type === 'fixed_amount' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold bg-emerald-100 text-emerald-800">
                                  -${Number(d.discount_value).toFixed(2)} CAD
                                </span>
                              )}
                              {d.discount_type === 'free_shipping_product' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold bg-teal-100 text-teal-800">
                                  🚚 Free Shipping (Product)
                                </span>
                              )}
                              {d.discount_type === 'free_shipping_code' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold bg-amber-100 text-amber-900">
                                  🚚 Free Shipping (Code)
                                </span>
                              )}
                            </td>

                            <td className="py-3.5 px-3">
                              {d.applies_to === 'selected_products' ? (
                                <div className="flex flex-wrap gap-1 max-w-xs">
                                  {selectedProds.length > 0 ? (
                                    selectedProds.map((p) => (
                                      <span
                                        key={p.id}
                                        className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-800 text-xs font-medium truncate max-w-[180px]"
                                      >
                                        {p.name}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-xs text-gray-500">
                                      {(d.product_ids || []).length} product(s) selected
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs font-semibold text-gray-700">
                                  All Store Products
                                </span>
                              )}
                            </td>

                            <td className="py-3.5 px-3 text-xs text-gray-600">
                              {d.no_time_limit ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-teal-50 text-teal-800 font-bold">
                                  ∞ No Time Limit
                                </span>
                              ) : (
                                <div className="space-y-0.5">
                                  <div>
                                    <span className="text-gray-400">From:</span>{' '}
                                    <span className="font-medium text-gray-800">
                                      {d.starts_at
                                        ? String(d.starts_at).replace('T', ' ').slice(0, 16)
                                        : 'Immediate'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-400">Until:</span>{' '}
                                    <span className="font-medium text-gray-800">
                                      {d.ends_at
                                        ? String(d.ends_at).replace('T', ' ').slice(0, 16)
                                        : 'No End Date'}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </td>

                            <td className="py-3.5 px-3">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-xs font-extrabold ${
                                    isExhausted ? 'text-red-600' : 'text-gray-900'
                                  }`}
                                  data-testid={`discount-usage-${d.id}`}
                                >
                                  {maxUses !== null
                                    ? `${usedCount} / ${maxUses} uses`
                                    : `${usedCount} used (∞)`}
                                </span>
                                {usedCount > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleResetDiscountUsage(d)}
                                    className="text-[11px] text-indigo-600 hover:underline font-semibold cursor-pointer"
                                    title="Reset usage counter to 0"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            </td>

                            <td className="py-3.5 px-3">
                              {!d.is_active ? (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                                  Paused
                                </span>
                              ) : isExhausted ? (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                                  Max Uses Reached
                                </span>
                              ) : d.status_label === 'expired' ? (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                                  Expired
                                </span>
                              ) : d.status_label === 'scheduled' ? (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                                  Scheduled
                                </span>
                              ) : (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                  Active
                                </span>
                              )}
                            </td>

                            <td className="py-3.5 px-3 text-right">
                              <div className="inline-flex items-center justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleToggleDiscountActive(d)}
                                  data-testid={`toggle-discount-btn-${d.id}`}
                                  className={`px-2.5 py-1 rounded-lg text-xs font-bold border cursor-pointer ${
                                    d.is_active
                                      ? 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-200'
                                      : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200'
                                  }`}
                                >
                                  {d.is_active ? 'Pause' : 'Enable'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openEditDiscountModal(d)}
                                  data-testid={`edit-discount-btn-${d.id}`}
                                  className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 cursor-pointer"
                                  title="Edit Discount Rule"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteDiscount(d)}
                                  data-testid={`delete-discount-btn-${d.id}`}
                                  className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer"
                                  title="Delete Discount Rule"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* MODAL: ADD / EDIT PRODUCT */}
      {isProductModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-modal-title"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-5">
              <h2 id="product-modal-title" className="text-xl font-extrabold text-gray-900">
                {productForm.id ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button
                type="button"
                onClick={() => setIsProductModalOpen(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveProduct} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="prod-name"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Product Title *
                  </label>
                  <input
                    id="prod-name"
                    type="text"
                    required
                    value={productForm.name}
                    onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="prod-sku"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    SKU *
                  </label>
                  <input
                    id="prod-sku"
                    type="text"
                    required
                    value={productForm.sku}
                    onChange={(e) => setProductForm((p) => ({ ...p, sku: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label
                    htmlFor="prod-price"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Price ({productForm.currency}) *
                  </label>
                  <input
                    id="prod-price"
                    type="number"
                    step="0.01"
                    required
                    value={productForm.price}
                    onChange={(e) => setProductForm((p) => ({ ...p, price: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="prod-compare-price"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Compare-At Price
                  </label>
                  <input
                    id="prod-compare-price"
                    type="number"
                    step="0.01"
                    value={productForm.compare_at_price}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, compare_at_price: e.target.value }))
                    }
                    placeholder="Optional"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="prod-stock"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Stock Quantity
                  </label>
                  <input
                    id="prod-stock"
                    type="number"
                    value={productForm.stock_quantity}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, stock_quantity: e.target.value }))
                    }
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="prod-category"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Category
                  </label>
                  <select
                    id="prod-category"
                    value={productForm.category}
                    onChange={(e) => setProductForm((p) => ({ ...p, category: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm bg-white"
                  >
                    <option value="Books & Journals">Books &amp; Journals</option>
                    <option value="Bundles & Kits">Bundles &amp; Kits</option>
                    <option value="Accessories">Accessories</option>
                    <option value="Posters & Guides">Posters &amp; Guides</option>
                    <option value="Gift Cards">Gift Cards</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="prod-type"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Fulfillment Type
                  </label>
                  <select
                    id="prod-type"
                    value={productForm.product_type}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, product_type: e.target.value }))
                    }
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm bg-white"
                  >
                    <option value="physical">Physical Product (Requires Shipping)</option>
                    <option value="digital">Digital / Instant Delivery (Free Shipping)</option>
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="prod-short-desc"
                  className="block text-xs font-bold uppercase text-gray-600 mb-1"
                >
                  Short Summary
                </label>
                <input
                  id="prod-short-desc"
                  type="text"
                  value={productForm.short_description}
                  onChange={(e) =>
                    setProductForm((p) => ({ ...p, short_description: e.target.value }))
                  }
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="prod-full-desc"
                  className="block text-xs font-bold uppercase text-gray-600 mb-1"
                >
                  Full Description
                </label>
                <textarea
                  id="prod-full-desc"
                  rows={3}
                  value={productForm.description}
                  onChange={(e) => setProductForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                />
              </div>

              {/* Image Upload & Gallery Management */}
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase text-gray-700">
                    Product Pictures &amp; Gallery
                  </span>
                  <label className="cursor-pointer px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold inline-flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" />
                    {uploadingImage ? 'Uploading...' : 'Upload Image File'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                <div>
                  <label
                    htmlFor="prod-primary-img"
                    className="block text-xs font-semibold text-gray-600 mb-1"
                  >
                    Primary Image URL
                  </label>
                  <input
                    id="prod-primary-img"
                    type="text"
                    value={productForm.primary_image_url}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, primary_image_url: e.target.value }))
                    }
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newGalleryUrl}
                    onChange={(e) => setNewGalleryUrl(e.target.value)}
                    placeholder="Add extra gallery image path or URL (e.g. /Images/shop/ultimate-kit.svg)"
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddGalleryUrl}
                    className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs font-bold"
                  >
                    Add to Gallery
                  </button>
                </div>

                {Array.isArray(productForm.gallery_images) &&
                  productForm.gallery_images.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {productForm.gallery_images.map((imgUrl, idx) => (
                        <div
                          key={`${imgUrl}-${idx}`}
                          className="relative group w-16 h-16 rounded-lg border border-gray-300 bg-white p-1 flex items-center justify-center"
                        >
                          <img
                            src={imgUrl}
                            alt={`Gallery ${idx + 1}`}
                            className="max-h-full max-w-full object-contain"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveGalleryImage(imgUrl)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px]"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <div>
                <label
                  htmlFor="prod-variants"
                  className="block text-xs font-bold uppercase text-gray-600 mb-1"
                >
                  Options / Variants (Comma-separated)
                </label>
                <input
                  id="prod-variants"
                  type="text"
                  value={productForm.variantsText}
                  onChange={(e) => setProductForm((p) => ({ ...p, variantsText: e.target.value }))}
                  placeholder="e.g. Physical Book, Digital E-Book ($14.99)"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                />
              </div>

              <div className="flex flex-wrap items-center gap-6 pt-2">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={productForm.is_active}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, is_active: e.target.checked }))
                    }
                    className="rounded text-indigo-600"
                  />
                  Active in Storefront
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={productForm.is_featured}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, is_featured: e.target.checked }))
                    }
                    className="rounded text-indigo-600"
                  />
                  Featured Hero Product
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={productForm.track_inventory}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, track_inventory: e.target.checked }))
                    }
                    className="rounded text-indigo-600"
                  />
                  Track Inventory Stock
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-teal-800 bg-teal-50 px-2.5 py-1 rounded-lg border border-teal-200">
                  <input
                    type="checkbox"
                    checked={Boolean(productForm.free_shipping)}
                    onChange={(e) =>
                      setProductForm((p) => ({ ...p, free_shipping: e.target.checked }))
                    }
                    data-testid="product-free-shipping-checkbox"
                    className="rounded text-teal-600"
                  />
                  🚚 Free Shipping (No Time Limit)
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsProductModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-product-submit"
                  className="px-5 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm shadow"
                >
                  {productForm.id ? 'Save Product Changes' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: CREATE / EDIT DISCOUNT & PROMOTION RULE */}
      {isDiscountModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="discount-modal-title"
          data-testid="admin-discount-modal"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-5">
              <div>
                <h2 id="discount-modal-title" className="text-xl font-extrabold text-gray-900">
                  {discountForm.id ? 'Edit Discount / Promotion Rule' : 'Create Discount / Promotion Rule'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure product percentage sales, fixed dollar discounts, or free shipping rules.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDiscountModalOpen(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveDiscountForm} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                    Discount Model Type *
                  </label>
                  <select
                    value={discountForm.discount_type}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setDiscountForm((prev) => ({
                        ...prev,
                        discount_type: nextType,
                        trigger_type:
                          nextType === 'free_shipping_code'
                            ? 'code'
                            : nextType === 'free_shipping_product'
                            ? 'automatic'
                            : prev.trigger_type,
                        applies_to:
                          nextType === 'free_shipping_product'
                            ? 'selected_products'
                            : prev.applies_to,
                        no_time_limit:
                          nextType === 'free_shipping_product' ? true : prev.no_time_limit
                      }));
                    }}
                    data-testid="discount-type-select"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-bold bg-white"
                  >
                    <option value="percentage">1. Percentage Discount (% Off)</option>
                    <option value="fixed_amount">2. Fixed Amount Discount ($ CAD Off)</option>
                    <option value="free_shipping_product">
                      3. Free Shipping for Specific Products (No Time Limit)
                    </option>
                    <option value="free_shipping_code">
                      4. Free Shipping by Promo Code (Dates &amp; Max Uses)
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                    Promotion Display Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={discountForm.title}
                    onChange={(e) =>
                      setDiscountForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    data-testid="discount-title-input"
                    placeholder="e.g. Summer Book 20% Off"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              {/* Trigger & Promo Code */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                    Activation Method
                  </label>
                  <select
                    value={
                      discountForm.discount_type === 'free_shipping_code'
                        ? 'code'
                        : discountForm.discount_type === 'free_shipping_product'
                        ? 'automatic'
                        : discountForm.trigger_type
                    }
                    disabled={
                      discountForm.discount_type === 'free_shipping_code' ||
                      discountForm.discount_type === 'free_shipping_product'
                    }
                    onChange={(e) =>
                      setDiscountForm((prev) => ({ ...prev, trigger_type: e.target.value }))
                    }
                    data-testid="discount-trigger-select"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm bg-white disabled:bg-gray-100"
                  >
                    <option value="automatic">Automatic (Applied Automatically in Cart)</option>
                    <option value="code">Promo Code (Customer Enters Code at Checkout)</option>
                  </select>
                </div>

                {(discountForm.trigger_type === 'code' ||
                  discountForm.discount_type === 'free_shipping_code') &&
                  discountForm.discount_type !== 'free_shipping_product' && (
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                        Promo Code *
                      </label>
                      <input
                        type="text"
                        required
                        value={discountForm.code}
                        onChange={(e) =>
                          setDiscountForm((prev) => ({
                            ...prev,
                            code: e.target.value.toUpperCase()
                          }))
                        }
                        data-testid="discount-code-input"
                        placeholder="e.g. FREESHIP25 or SAVE10"
                        className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-mono uppercase"
                      />
                    </div>
                  )}
              </div>

              {/* Value & Min Order (shown for percentage or fixed_amount) */}
              {(discountForm.discount_type === 'percentage' ||
                discountForm.discount_type === 'fixed_amount') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                      {discountForm.discount_type === 'percentage'
                        ? 'Discount Percentage (%) *'
                        : 'Fixed Discount Amount ($ CAD) *'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={discountForm.discount_type === 'percentage' ? '100' : undefined}
                      required
                      value={discountForm.discount_value}
                      onChange={(e) =>
                        setDiscountForm((prev) => ({
                          ...prev,
                          discount_value: e.target.value
                        }))
                      }
                      data-testid="discount-value-input"
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                      Minimum Order Subtotal ($ CAD)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={discountForm.min_order_amount}
                      onChange={(e) =>
                        setDiscountForm((prev) => ({
                          ...prev,
                          min_order_amount: e.target.value
                        }))
                      }
                      data-testid="discount-min-order-input"
                      className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Product Scope Selection */}
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <label className="text-xs font-bold uppercase text-gray-700">
                    Target Products Scope
                  </label>
                  <select
                    value={
                      discountForm.discount_type === 'free_shipping_product'
                        ? 'selected_products'
                        : discountForm.applies_to
                    }
                    disabled={discountForm.discount_type === 'free_shipping_product'}
                    onChange={(e) =>
                      setDiscountForm((prev) => ({ ...prev, applies_to: e.target.value }))
                    }
                    data-testid="discount-applies-to-select"
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-bold bg-white disabled:bg-gray-100"
                  >
                    <option value="selected_products">Selected Product(s) Only</option>
                    <option value="all">All Store Products (Entire Cart)</option>
                  </select>
                </div>

                {(discountForm.applies_to === 'selected_products' ||
                  discountForm.discount_type === 'free_shipping_product') && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto pt-1">
                    {adminProducts.map((prod) => {
                      const checked = (discountForm.product_ids || [])
                        .map(Number)
                        .includes(Number(prod.id));
                      return (
                        <label
                          key={prod.id}
                          className={`flex items-center gap-2.5 p-2 rounded-lg border text-xs cursor-pointer transition ${
                            checked
                              ? 'bg-indigo-50 border-indigo-400 text-indigo-950 font-bold'
                              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleToggleDiscountProduct(prod.id)}
                            data-testid={`discount-product-checkbox-${prod.id}`}
                            className="rounded text-indigo-600"
                          />
                          <span className="truncate flex-1">{prod.name}</span>
                          <span className="text-[11px] text-gray-500">
                            ${Number(prod.price || 0).toFixed(2)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Time Window & Usage Limits */}
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase text-gray-700">
                    Schedule &amp; Usage / Implementations Limit
                  </span>
                  <label className="inline-flex items-center gap-2 text-xs font-bold text-teal-800">
                    <input
                      type="checkbox"
                      checked={
                        discountForm.discount_type === 'free_shipping_product'
                          ? true
                          : Boolean(discountForm.no_time_limit)
                      }
                      disabled={discountForm.discount_type === 'free_shipping_product'}
                      onChange={(e) =>
                        setDiscountForm((prev) => ({
                          ...prev,
                          no_time_limit: e.target.checked
                        }))
                      }
                      data-testid="discount-no-time-limit-checkbox"
                      className="rounded text-teal-600"
                    />
                    No Time Limit (Permanent)
                  </label>
                </div>

                {!discountForm.no_time_limit &&
                  discountForm.discount_type !== 'free_shipping_product' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Start Date &amp; Time
                        </label>
                        <input
                          type="datetime-local"
                          value={discountForm.starts_at}
                          onChange={(e) =>
                            setDiscountForm((prev) => ({
                              ...prev,
                              starts_at: e.target.value
                            }))
                          }
                          data-testid="discount-starts-at-input"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          End Date &amp; Time
                        </label>
                        <input
                          type="datetime-local"
                          value={discountForm.ends_at}
                          onChange={(e) =>
                            setDiscountForm((prev) => ({
                              ...prev,
                              ends_at: e.target.value
                            }))
                          }
                          data-testid="discount-ends-at-input"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white"
                        />
                      </div>
                    </div>
                  )}

                <div className="pt-1">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Maximum Number of Uses / Implementations (Leave blank for unlimited)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={discountForm.max_uses}
                    onChange={(e) =>
                      setDiscountForm((prev) => ({ ...prev, max_uses: e.target.value }))
                    }
                    data-testid="discount-max-uses-input"
                    placeholder="e.g. 25 (or leave blank for unlimited uses)"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={Boolean(discountForm.is_active)}
                    onChange={(e) =>
                      setDiscountForm((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                    data-testid="discount-active-checkbox"
                    className="rounded text-indigo-600"
                  />
                  Rule Active Immediately
                </label>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsDiscountModalOpen(false)}
                    className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="save-discount-submit"
                    className="px-5 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm shadow cursor-pointer"
                  >
                    {discountForm.id ? 'Save Discount Changes' : 'Create Discount Rule'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ORDER FULFILLMENT & TRACKING */}
      {selectedOrder && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-modal-title"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-4">
              <div>
                <h2 id="order-modal-title" className="text-xl font-extrabold text-gray-900">
                  Order {selectedOrder.order_number}
                </h2>
                <p className="text-xs text-gray-500">
                  Customer: {selectedOrder.customer_name} ({selectedOrder.customer_email})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedOrder(null)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Order Items & Breakdown */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-5 text-sm">
              <div className="font-bold text-gray-800 mb-2">Items Ordered</div>
              <div className="divide-y divide-gray-200">
                {(selectedOrder.items || []).map((item, idx) => (
                  <div key={idx} className="py-2 flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-gray-900">
                        {item.product_name || item.name}
                      </span>{' '}
                      <span className="text-xs text-gray-500">× {item.quantity}</span>
                      {item.variant_info && (
                        <span className="ml-2 text-xs text-indigo-600">({item.variant_info})</span>
                      )}
                    </div>
                    <span className="font-bold text-gray-900">
                      ${Number(item.line_total || item.unit_price * item.quantity || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 mt-3 pt-2 space-y-1 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>${Number(selectedOrder.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping:</span>
                  <span>${Number(selectedOrder.shipping_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax ({selectedOrder.tax_label || 'HST'}):</span>
                  <span>${Number(selectedOrder.tax_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-extrabold text-gray-900 pt-1">
                  <span>Total ({selectedOrder.currency || 'CAD'}):</span>
                  <span>
                    ${Number(selectedOrder.total_amount || 0).toFixed(2)}{' '}
                    {selectedOrder.currency || 'CAD'}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleUpdateOrder} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="ord-fulfillment-status"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Fulfillment Status
                  </label>
                  <select
                    id="ord-fulfillment-status"
                    value={orderUpdateForm.fulfillment_status}
                    onChange={(e) =>
                      setOrderUpdateForm((f) => ({ ...f, fulfillment_status: e.target.value }))
                    }
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm bg-white font-semibold"
                  >
                    <option value="unfulfilled">Unfulfilled</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="ord-carrier"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Shipping Carrier
                  </label>
                  <input
                    id="ord-carrier"
                    type="text"
                    value={orderUpdateForm.tracking_carrier}
                    onChange={(e) =>
                      setOrderUpdateForm((f) => ({ ...f, tracking_carrier: e.target.value }))
                    }
                    placeholder="Canada Post, UPS, FedEx..."
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="ord-tracking-num"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Tracking Number
                  </label>
                  <input
                    id="ord-tracking-num"
                    type="text"
                    value={orderUpdateForm.tracking_number}
                    onChange={(e) =>
                      setOrderUpdateForm((f) => ({ ...f, tracking_number: e.target.value }))
                    }
                    placeholder="e.g. CP123456789CA"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-mono"
                  />
                </div>
                <div>
                  <label
                    htmlFor="ord-tracking-url"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Carrier Tracking URL
                  </label>
                  <input
                    id="ord-tracking-url"
                    type="url"
                    value={orderUpdateForm.tracking_url}
                    onChange={(e) =>
                      setOrderUpdateForm((f) => ({ ...f, tracking_url: e.target.value }))
                    }
                    placeholder="https://..."
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="ord-admin-notes"
                  className="block text-xs font-bold uppercase text-gray-600 mb-1"
                >
                  Internal Admin Notes
                </label>
                <textarea
                  id="ord-admin-notes"
                  rows={2}
                  value={orderUpdateForm.admin_notes}
                  onChange={(e) =>
                    setOrderUpdateForm((f) => ({ ...f, admin_notes: e.target.value }))
                  }
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                />
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 font-medium">
                <input
                  type="checkbox"
                  checked={orderUpdateForm.send_shipping_email}
                  onChange={(e) =>
                    setOrderUpdateForm((f) => ({ ...f, send_shipping_email: e.target.checked }))
                  }
                  className="rounded text-indigo-600"
                />
                Send shipping &amp; tracking notification email to customer when marked Shipped
              </label>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setSelectedOrder(null)}
                  className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm"
                >
                  Close
                </button>
                <button
                  type="submit"
                  data-testid="save-order-fulfillment-btn"
                  className="px-5 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm shadow"
                >
                  Save Fulfillment &amp; Tracking
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT TAX ZONE */}
      {isTaxZoneModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tax-zone-modal-title"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
              <h2 id="tax-zone-modal-title" className="text-lg font-extrabold text-gray-900">
                {taxZoneForm.id ? 'Edit Tax Zone' : 'Add Regional Tax Zone'}
              </h2>
              <button
                type="button"
                onClick={() => setIsTaxZoneModalOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveTaxZone} className="space-y-4">
              <div>
                <label
                  htmlFor="tz-name"
                  className="block text-xs font-bold uppercase text-gray-600 mb-1"
                >
                  Zone Display Name *
                </label>
                <input
                  id="tz-name"
                  type="text"
                  required
                  value={taxZoneForm.name}
                  onChange={(e) => setTaxZoneForm((z) => ({ ...z, name: e.target.value }))}
                  placeholder="e.g. Ontario (Default HST)"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="tz-country"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Country Code
                  </label>
                  <input
                    id="tz-country"
                    type="text"
                    value={taxZoneForm.country_code}
                    onChange={(e) =>
                      setTaxZoneForm((z) => ({ ...z, country_code: e.target.value }))
                    }
                    placeholder="CA, US, *"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-mono uppercase"
                  />
                </div>
                <div>
                  <label
                    htmlFor="tz-region"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Province / State Code(s)
                  </label>
                  <input
                    id="tz-region"
                    type="text"
                    value={taxZoneForm.region_code}
                    onChange={(e) =>
                      setTaxZoneForm((z) => ({ ...z, region_code: e.target.value }))
                    }
                    placeholder="ON, BC, QC or *"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-mono uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="tz-tax-name"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Tax Name *
                  </label>
                  <input
                    id="tz-tax-name"
                    type="text"
                    required
                    value={taxZoneForm.tax_name}
                    onChange={(e) => setTaxZoneForm((z) => ({ ...z, tax_name: e.target.value }))}
                    placeholder="HST, GST+PST, GST"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="tz-tax-rate"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Tax Rate (%) *
                  </label>
                  <input
                    id="tz-tax-rate"
                    type="number"
                    step="0.001"
                    required
                    value={taxZoneForm.tax_rate}
                    onChange={(e) => setTaxZoneForm((z) => ({ ...z, tax_rate: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2 pt-1">
                <label className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                  <input
                    type="checkbox"
                    checked={taxZoneForm.apply_to_shipping}
                    onChange={(e) =>
                      setTaxZoneForm((z) => ({ ...z, apply_to_shipping: e.target.checked }))
                    }
                    className="rounded text-indigo-600"
                  />
                  Apply tax rate to shipping fee
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                  <input
                    type="checkbox"
                    checked={taxZoneForm.is_default}
                    onChange={(e) =>
                      setTaxZoneForm((z) => ({ ...z, is_default: e.target.checked }))
                    }
                    className="rounded text-indigo-600"
                  />
                  Set as default fallback tax zone
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsTaxZoneModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-tax-zone-submit"
                  className="px-5 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm shadow"
                >
                  Save Tax Zone
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* OVERLAY: PLAYER MATCHES LOGGED IN A GRID */}
      {selectedPlayerOverlay && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="player-matches-overlay-title"
          data-testid="player-matches-overlay"
        >
          <div className="bg-white rounded-2xl max-w-5xl w-full shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col overflow-hidden">
            {/* Overlay Header */}
            <div className="bg-slate-900 text-white px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300 font-extrabold text-lg">
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 id="player-matches-overlay-title" className="text-xl font-extrabold text-white">
                      {selectedPlayerOverlay.user.full_name ||
                        `${selectedPlayerOverlay.user.first_name || ''} ${
                          selectedPlayerOverlay.user.last_name || ''
                        }`.trim() ||
                        selectedPlayerOverlay.user.email}{' '}
                      — Logged Matches
                    </h2>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                      {selectedPlayerOverlay.user.role === 'admin' ? 'Admin' : 'TennisLog Player'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">
                    {selectedPlayerOverlay.user.email}
                    {selectedPlayerOverlay.user.club_name
                      ? ` • ${selectedPlayerOverlay.user.club_name}`
                      : ''}
                    {selectedPlayerOverlay.user.utr ? ` • UTR ${selectedPlayerOverlay.user.utr}` : ''}
                    {selectedPlayerOverlay.user.wtn ? ` • WTN ${selectedPlayerOverlay.user.wtn}` : ''}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedPlayerOverlay(null)}
                data-testid="close-player-matches-overlay"
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white cursor-pointer"
                aria-label="Close player matches overlay"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Summary KPI Strip & Search/Filter Bar */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-2xs">
                  <div className="text-[11px] font-bold uppercase text-gray-400">Total Logged</div>
                  <div className="text-xl font-extrabold text-gray-900">
                    {selectedPlayerOverlay.summary?.total_matches ??
                      selectedPlayerOverlay.matches.length}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-2xs">
                  <div className="text-[11px] font-bold uppercase text-gray-400">Completed</div>
                  <div className="text-xl font-extrabold text-indigo-700">
                    {selectedPlayerOverlay.summary?.completed_matches ?? 0}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-2xs">
                  <div className="text-[11px] font-bold uppercase text-gray-400">Wins</div>
                  <div className="text-xl font-extrabold text-emerald-700">
                    {selectedPlayerOverlay.summary?.wins ?? 0}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-2xs">
                  <div className="text-[11px] font-bold uppercase text-gray-400">Losses</div>
                  <div className="text-xl font-extrabold text-red-600">
                    {selectedPlayerOverlay.summary?.losses ?? 0}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-2xs col-span-2 sm:col-span-1">
                  <div className="text-[11px] font-bold uppercase text-gray-400">Win Rate</div>
                  <div className="text-xl font-extrabold text-amber-600">
                    {selectedPlayerOverlay.summary?.win_rate ?? 0}%
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {[
                    { key: 'all', label: 'All Matches' },
                    { key: 'completed', label: 'Completed' },
                    { key: 'inprogress', label: 'In Progress' }
                  ].map((st) => (
                    <button
                      key={st.key}
                      type="button"
                      onClick={() => setMatchesOverlayStatusFilter(st.key)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                        matchesOverlayStatusFilter === st.key
                          ? 'bg-indigo-700 text-white'
                          : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {st.label}
                    </button>
                  ))}
                </div>

                <div className="relative flex-1 max-w-sm">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={matchesOverlaySearch}
                    onChange={(e) => setMatchesOverlaySearch(e.target.value)}
                    data-testid="player-matches-search"
                    placeholder="Filter matches by opponent, title, score, or court..."
                    className="w-full pl-9 pr-3 py-1.5 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Matches Grid Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {selectedPlayerOverlay.loading ? (
                <div className="py-16 text-center text-gray-500 text-sm font-semibold">
                  Loading player matches...
                </div>
              ) : (
                <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                  data-testid="player-matches-grid"
                >
                  {filteredOverlayMatches.length === 0 ? (
                    <div
                      className="col-span-full text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl"
                      data-testid="player-matches-empty"
                    >
                      <Trophy className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-700 font-bold">No logged matches found for this player</p>
                      <p className="text-xs text-gray-500 mt-1">
                        When this player logs matches in TennisLog, they will appear here in the match grid.
                      </p>
                    </div>
                  ) : (
                    filteredOverlayMatches.map((m) => {
                      const isWon = m.winner === 'me' || m.winner === 'player1';
                      const isLost = m.winner === 'opponent' || m.winner === 'player2';
                      const isCompleted = m.status === 'completed';

                      return (
                        <div
                          key={m.match_id}
                          data-testid={`player-match-card-${m.match_id}`}
                          className="rounded-2xl border border-gray-200 bg-white p-4 shadow-xs hover:shadow-md transition flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div>
                                <h3 className="font-extrabold text-gray-900 text-sm line-clamp-1">
                                  {m.match_name || `Match #${m.match_id}`}
                                </h3>
                                <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-0.5">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    {m.match_date
                                      ? new Date(m.match_date).toLocaleDateString()
                                      : 'Recent'}
                                  </span>
                                  {m.location && <span>• {m.location}</span>}
                                </div>
                              </div>

                              <span
                                className={`px-2.5 py-0.5 rounded-full text-[11px] font-extrabold uppercase flex-shrink-0 ${
                                  !isCompleted
                                    ? 'bg-amber-100 text-amber-800'
                                    : isWon
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : isLost
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {!isCompleted
                                  ? 'In Progress'
                                  : isWon
                                  ? 'Won'
                                  : isLost
                                  ? 'Lost'
                                  : 'Completed'}
                              </span>
                            </div>

                            <div className="my-3 p-3 rounded-xl bg-slate-50 border border-slate-200/80">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-gray-500 font-medium">Opponent</span>
                                <span className="font-bold text-gray-900">
                                  vs. {m.opponent_name || 'Opponent'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between pt-2 border-t border-slate-200/70">
                                <span className="text-xs font-semibold text-gray-500">Set Score</span>
                                <span className="font-mono font-extrabold text-base text-indigo-700">
                                  {m.score || '0-0'}
                                </span>
                              </div>
                            </div>

                            {m.description && (
                              <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                                {m.description}
                              </p>
                            )}
                          </div>

                          <div className="pt-2.5 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500 font-medium">
                            <span>
                              Format: Best of {m.match_config?.numberOfSets || 3} Sets
                            </span>
                            <span>
                              Sets: {m.my_sets_won ?? 0} - {m.opponent_sets_won ?? 0}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setSelectedPlayerOverlay(null)}
                className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs cursor-pointer"
              >
                Close Matches Overlay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ADMIN PASSWORD RESET (DIRECT OR EMAIL LINK) */}
      {passwordResetModalUser && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="password-reset-modal-title"
          data-testid="admin-password-reset-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <h2 id="password-reset-modal-title" className="text-lg font-extrabold text-gray-900">
                  Reset User Password
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Account: <strong>{passwordResetModalUser.email}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPasswordResetModalUser(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Option 1: Direct Password Reset */}
            <form onSubmit={handleDirectPasswordReset} className="space-y-3">
              <div>
                <label
                  htmlFor="admin-new-password-input"
                  className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1"
                >
                  Option 1 — Set New Password Directly
                </label>
                <input
                  id="admin-new-password-input"
                  type="text"
                  required
                  minLength={6}
                  value={directNewPassword}
                  onChange={(e) => setDirectNewPassword(e.target.value)}
                  data-testid="direct-reset-password-input"
                  placeholder="Enter new password (min 6 chars)..."
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-mono"
                />
              </div>
              <button
                type="submit"
                data-testid="confirm-direct-password-reset-btn"
                className="w-full py-2.5 px-4 bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-xs rounded-xl shadow inline-flex items-center justify-center gap-2 cursor-pointer"
              >
                <Key className="w-4 h-4" />
                Save New Password Immediately
              </button>
            </form>

            {/* Option 2: Initiate Password Reset Email */}
            <div className="pt-4 border-t border-gray-200 space-y-2.5">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-600">
                Option 2 — Initiate Password Reset to User Email
              </div>
              <p className="text-xs text-gray-500">
                Generates a secure password reset token and emails a password reset link directly to{' '}
                <strong>{passwordResetModalUser.email}</strong>.
              </p>
              <button
                type="button"
                onClick={() => handleSendPasswordResetEmail(passwordResetModalUser)}
                data-testid="modal-send-reset-email-btn"
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow inline-flex items-center justify-center gap-2 cursor-pointer"
              >
                <Mail className="w-4 h-4" />
                Send Password Reset Email to User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT USER OR ADMIN */}
      {isUserFormModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-form-modal-title"
          data-testid="admin-user-form-modal"
        >
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
              <h2 id="user-form-modal-title" className="text-lg font-extrabold text-gray-900">
                {userForm.id ? 'Edit User Account' : 'Create TennisLog / Admin User'}
              </h2>
              <button
                type="button"
                onClick={() => setIsUserFormModalOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveUserForm} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="user-first-name"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    First Name *
                  </label>
                  <input
                    id="user-first-name"
                    type="text"
                    required
                    value={userForm.first_name}
                    onChange={(e) => setUserForm((f) => ({ ...f, first_name: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="user-last-name"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Last Name
                  </label>
                  <input
                    id="user-last-name"
                    type="text"
                    value={userForm.last_name}
                    onChange={(e) => setUserForm((f) => ({ ...f, last_name: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="user-email-input"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Email Address *
                  </label>
                  <input
                    id="user-email-input"
                    type="email"
                    required
                    value={userForm.email}
                    onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="user-role-select"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Account Role *
                  </label>
                  <select
                    id="user-role-select"
                    value={userForm.role}
                    onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-bold bg-white"
                  >
                    <option value="user">TennisLog Player (Standard)</option>
                    <option value="admin">Administrator (Full Backoffice)</option>
                  </select>
                </div>
              </div>

              {!userForm.id && (
                <div>
                  <label
                    htmlFor="user-password-input"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Initial Password *
                  </label>
                  <input
                    id="user-password-input"
                    type="text"
                    required
                    minLength={6}
                    value={userForm.password}
                    onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="At least 6 characters"
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-mono"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label
                    htmlFor="user-club-input"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    Tennis Club
                  </label>
                  <input
                    id="user-club-input"
                    type="text"
                    value={userForm.club_name}
                    onChange={(e) => setUserForm((f) => ({ ...f, club_name: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="user-utr-input"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    UTR Rating
                  </label>
                  <input
                    id="user-utr-input"
                    type="text"
                    value={userForm.utr}
                    onChange={(e) => setUserForm((f) => ({ ...f, utr: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label
                    htmlFor="user-wtn-input"
                    className="block text-xs font-bold uppercase text-gray-600 mb-1"
                  >
                    WTN Rating
                  </label>
                  <input
                    id="user-wtn-input"
                    type="text"
                    value={userForm.wtn}
                    onChange={(e) => setUserForm((f) => ({ ...f, wtn: e.target.value }))}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsUserFormModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-user-form-submit"
                  className="px-5 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm shadow cursor-pointer"
                >
                  {userForm.id ? 'Save User Changes' : 'Create User Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
